import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contractAPI, propertyAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import ConfirmModal from '../Common/ConfirmModal';
import './ContractCreate.css';

const ContractCreate = ({ isView = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const isEdit = id && parseInt(id) > 0;
  
  // Get contract type from URL params
  const [contractType, setContractType] = useState(() => {
    // Initialize from URL parameter on mount
    const searchParams = new URLSearchParams(window.location.search);
    const typeParam = searchParams.get('type');
    const initialType = typeParam === 'commercial' ? 1 : 0;
    console.log('Initial contract type from URL:', typeParam, '->', initialType);
    return initialType;
  }); // 0 = Residential, 1 = Commercial
  
  // Update contract type when URL changes or on mount
  useEffect(() => {
    if (!isEdit) {
      // Only update from URL if not editing (when editing, contractType comes from loaded contract)
      const newSearchParams = new URLSearchParams(location.search);
      const newContractTypeParam = newSearchParams.get('type');
      console.log('Contract type from URL (useEffect):', newContractTypeParam, 'Current contractType:', contractType, 'location.search:', location.search);
      if (newContractTypeParam === 'commercial') {
        console.log('Setting contractType to 1 (Commercial)');
        setContractType(1);
      } else if (newContractTypeParam === 'residential') {
        console.log('Setting contractType to 0 (Residential)');
        setContractType(0);
      } else if (!newContractTypeParam && location.search === '') {
        // If no type parameter and no search params, check window.location as fallback
        const windowSearchParams = new URLSearchParams(window.location.search);
        const windowTypeParam = windowSearchParams.get('type');
        if (windowTypeParam === 'commercial') {
          console.log('Setting contractType to 1 (Commercial) from window.location');
          setContractType(1);
        } else if (windowTypeParam === 'residential') {
          console.log('Setting contractType to 0 (Residential) from window.location');
          setContractType(0);
        }
      }
    }
  }, [location.search, isEdit]);

  // Tab management
  const [activeTab, setActiveTab] = useState(1);
  const [tab1Saved, setTab1Saved] = useState(false);
  const [tab2Saved, setTab2Saved] = useState(false);
  const [tab3Saved, setTab3Saved] = useState(false);
  const [tab4Saved, setTab4Saved] = useState(false);
  
  // Financial Terms sub-tabs - removed, now using sections instead

  // Contract data
  const [contractId, setContractId] = useState(null);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [propertyId, setPropertyId] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);

  // Tab 2: Units
  const [units, setUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  // Tab 3: Parties
  const [lessor, setLessor] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [propertyOwnerships, setPropertyOwnerships] = useState([]);
  const [deletedOwnershipIds, setDeletedOwnershipIds] = useState([]); // Track deleted ownership IDs

  // Tab 4: Financial Terms
  const [financialTerms, setFinancialTerms] = useState({
    annualRentPerUnit: null,
    paymentFrequency: '',
    brokerageCommission: null,
    totalRent: null,
    totalServices: null,
    paymentMethod: '',
    paymentChannels: [],
    ibanNumber: '',
    ibanAccountHolderName: '',
    securityDepositEnabled: false,
    securityDepositAmount: null,
    dailyFineEnabled: false,
    dailyFineAmount: null,
    retainerEnabled: false,
    retainerAmount: null,
    retainerDeductionMethod: '',
    jointAccountNumber: '',
    brokerageOfficeFeeEnabled: false,
    brokerageOfficeFeePaidBy: '',
    brokerageOfficeFeeAmount: null,
    brokerageOfficeFeeDueDate: null,
    totalPaidAmount: null,
    paidServicesAmount: null,
    firstBillDueRent: null,
    firstBillDueServices: null,
    scheduledAmount: null,
    scheduledStartDate: null,
  });
  const [paymentDates, setPaymentDates] = useState([]);

  // Dropdowns
  const [dropdowns, setDropdowns] = useState({
    nationalities: [],
    identities: [],
    countries: [],
    cities: [],
    areas: [],
    districts: [],
    unitFacilities: []
  });

  // Modals
  const [showAddLessorModal, setShowAddLessorModal] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showUnitFacilitiesModal, setShowUnitFacilitiesModal] = useState(false);
  const [editingTenantIndex, setEditingTenantIndex] = useState(null);
  const [forceCommercialTenantForm, setForceCommercialTenantForm] = useState(false);
  const [editingOwnershipId, setEditingOwnershipId] = useState(null);
  const [currentUnitIndex, setCurrentUnitIndex] = useState(-1);
  const [lessorFormSubmitted, setLessorFormSubmitted] = useState(false);
  const [tenantFormSubmitted, setTenantFormSubmitted] = useState(false);
  const [lessorFormErrors, setLessorFormErrors] = useState({});
  const [tenantFormErrors, setTenantFormErrors] = useState({});

  // Lessor form data
  const [lessorFormData, setLessorFormData] = useState({
    agentNumber: '',
    nameAr: '',
    nameEng: '',
    dob: '',
    nationalityId: '',
    identityTypeId: '',
    identityNumber: '',
    mobileCountryCode: '',
    mobileNumber: '',
    emailId: '',
    bankId: '',
    bankAccountNumber: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    buildingNumber: '',
    additionalNumber: '',
    postalCode: ''
  });

  // Tenant form data
  const [tenantFormData, setTenantFormData] = useState({
    nameAr: '',
    nameEng: '',
    dob: '',
    nationalityId: '',
    identityTypeId: '',
    identityNumber: '',
    mobileCountryCode: '',
    mobileNumber: '',
    emailId: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    buildingNumber: '',
    additionalNumber: '',
    postalCode: '',
    // Commercial fields
    tradeNameAr: '',
    tradeNameEng: '',
    crNumber: '',
    vatNumber: '',
    telephoneNumber: '',
    faxNumber: '',
    website: '',
    // Additional commercial fields
    unifiedNumber: '',
    ownerIdType: '',
    facilityType: '',
    releasedBy: '',
    commerceRegistryConfirmationDate: '',
    crDate: '',
    commerceActivities: ''
  });

  // Unit facilities selection
  const [selectedUnitFacilities, setSelectedUnitFacilities] = useState({});

  // Supporting data
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load dropdowns on mount
  useEffect(() => {
    loadDropdowns();
  }, []);

  // Check for selected property from PropertySelect page
  useEffect(() => {
    const selectedPropertyIdFromStorage = sessionStorage.getItem('selectedPropertyId');
    if (selectedPropertyIdFromStorage) {
      const propertyId = parseInt(selectedPropertyIdFromStorage);
      if (!isNaN(propertyId) && propertyId > 0) {
        loadSelectedProperty(propertyId);
        sessionStorage.removeItem('selectedPropertyId');
      }
    }

    if (isEdit || isView) {
      loadContract();
    }
  }, [id]);

  // Regenerate payment dates when contract start date or payment frequency changes
  useEffect(() => {
    if (financialTerms.paymentFrequency && dateFrom && !isView) {
      generatePaymentDates(financialTerms.paymentFrequency, dateFrom, financialTerms.totalRent);
    }
  }, [dateFrom, financialTerms.paymentFrequency]);

  // Load dropdown data
  const loadDropdowns = async () => {
    try {
      const response = await propertyAPI.getDropdowns();
      const data = response.data || {};
      setDropdowns({
        nationalities: data.Nationalities || data.nationalities || [],
        identities: data.IdentityTypes || data.identityTypes || data.Identities || data.identities || [],
        countries: data.Countries || data.countries || [],
        cities: data.Cities || data.cities || [],
        areas: data.Areas || data.areas || [],
        districts: data.Districts || data.districts || [],
        banks: data.Banks || data.banks || [],
        unitFacilities: data.UnitFacilitiesAndAmenities || data.unitFacilitiesAndAmenities || []
      });
    } catch (err) {
      console.error('Failed to load dropdowns:', err);
    }
  };

  // Helper functions to filter location dropdowns hierarchically
  const getFilteredCities = (areaId) => {
    if (!areaId) return [];
    const cities = dropdowns.cities || [];
    return cities.filter(city => {
      const cityAreaId = city.areaId || city.AreaId;
      return cityAreaId === parseInt(areaId);
    });
  };

  const getFilteredDistricts = (cityId) => {
    if (!cityId) return [];
    const districts = dropdowns.districts || [];
    return districts.filter(district => {
      const districtCityId = district.cityId || district.CityId;
      return districtCityId === parseInt(cityId);
    });
  };

  // Handle area change for tenant form - reset city and district
  const handleTenantAreaChange = (areaId) => {
    setTenantFormData({
      ...tenantFormData,
      areaId: areaId,
      cityId: '', // Reset city when area changes
      districtId: '' // Reset district when area changes
    });
  };

  // Handle city change for tenant form - reset district
  const handleTenantCityChange = (cityId) => {
    setTenantFormData({
      ...tenantFormData,
      cityId: cityId,
      districtId: '' // Reset district when city changes
    });
  };

  // Handle area change for lessor form - reset city and district
  const handleLessorAreaChange = (areaId) => {
    setLessorFormData({
      ...lessorFormData,
      areaId: areaId,
      cityId: '', // Reset city when area changes
      districtId: '' // Reset district when area changes
    });
  };

  // Handle city change for lessor form - reset district
  const handleLessorCityChange = (cityId) => {
    setLessorFormData({
      ...lessorFormData,
      cityId: cityId,
      districtId: '' // Reset district when city changes
    });
  };

  const loadContract = async () => {
    try {
      setLoading(true);
      const response = await contractAPI.getById(id);
      const contract = response.data;
      
      setContractId(contract.id);
      setDateFrom(contract.dateFrom?.split('T')[0] || dateFrom);
      setDateTo(contract.dateTo?.split('T')[0] || dateTo);
      setPropertyId(contract.propertyId);
      setContractType(contract.contractType ?? 0); // Load contract type, default to Residential
      
      if (contract.property) {
        setSelectedProperty(contract.property);
      }
      
      // Load related data
      if (contract.propertyId) {
        await loadPropertyUnits(contract.propertyId);
        await loadPropertyOwnerships(contract.propertyId);
      }
      
      if (contract.contractUnits) {
        setSelectedUnits(contract.contractUnits.map(cu => ({
          propertyUnitId: cu.propertyUnitId,
          agreementStatus: cu.agreementStatus,
          facilities: cu.contractUnitFacilities?.map(cuf => ({
            facilityId: cuf.unitFacilitiesAndAmenitiesId,
            quantity: cuf.quantity
          })) || []
        })));
        setTab2Saved(true);
      }
      
      if (contract.contractLessor) {
        setLessor(contract.contractLessor);
        setTab3Saved(true);
      }
      
      if (contract.contractTenants) {
        setTenants(contract.contractTenants);
        setTab3Saved(true);
      }
      
      if (contract.contractFinancialTerms) {
        const ft = contract.contractFinancialTerms;
        // Parse PaymentChannels if it's a JSON string
        let paymentChannels = ft.paymentChannels || ft.PaymentChannels || [];
        if (typeof paymentChannels === 'string') {
          try {
            paymentChannels = JSON.parse(paymentChannels);
          } catch {
            // If not JSON, try splitting by comma
            paymentChannels = paymentChannels.split(',').filter(c => c.trim());
          }
        }
        
        setFinancialTerms({
          ...ft,
          paymentChannels: Array.isArray(paymentChannels) ? paymentChannels : []
        });
        
        // Load payment dates if available
        if (ft.paymentDates || ft.PaymentDates) {
          const dates = ft.paymentDates || ft.PaymentDates || [];
          setPaymentDates(Array.isArray(dates) ? dates : []);
        } else if (ft.paymentFrequency && dateFrom) {
          // Generate payment dates if frequency is set but dates don't exist
          generatePaymentDates(ft.paymentFrequency, dateFrom, ft.totalRent);
        }
        
        setTab4Saved(true);
      }
      
      setTab1Saved(true);
    } catch (err) {
      setErrorMessage(t('contract.create.messages.failedToLoadContract'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPropertyUnits = async (propId) => {
    try {
      const response = await contractAPI.getPropertyUnits(propId);
      setUnits(response.data || []);
    } catch (err) {
      console.error('Failed to load property units:', err);
    }
  };

  // Helper function to format and save ownerships to property
  const savePropertyOwnerships = async (ownershipsToSave = null, deletedIds = null) => {
    if (!propertyId) return;
    
    try {
      const ownershipsArray = ownershipsToSave !== null ? ownershipsToSave : propertyOwnerships;
      const deletedIdsArray = deletedIds !== null ? deletedIds : deletedOwnershipIds;
      
      const parseId = (value) => {
        if (!value) return null;
        if (Array.isArray(value)) {
          const parsed = parseInt(value[0]);
          return isNaN(parsed) ? null : parsed;
        }
        if (typeof value === 'string') {
          const parsed = parseInt(value);
          return isNaN(parsed) ? null : parsed;
        }
        if (typeof value === 'number') {
          return isNaN(value) ? null : value;
        }
        return null;
      };

      const formattedOwnerships = ownershipsArray.map(ownership => {
        let ownerIdValue = null;
        if (ownership.ownerId) {
          if (Array.isArray(ownership.ownerId)) {
            const firstVal = ownership.ownerId[0];
            if (firstVal && firstVal.toString().trim() !== '') {
              const parsed = parseInt(firstVal.toString());
              ownerIdValue = isNaN(parsed) ? null : parsed;
            }
          } else if (typeof ownership.ownerId === 'string') {
            const trimmed = ownership.ownerId.trim();
            if (trimmed !== '') {
              const parsed = parseInt(trimmed);
              ownerIdValue = isNaN(parsed) ? null : parsed;
            }
          } else if (typeof ownership.ownerId === 'number') {
            ownerIdValue = isNaN(ownership.ownerId) ? null : ownership.ownerId;
          }
        }
        
        const formatted = {};
        const oId = ownership.id || ownership.Id;
        if (oId) formatted.id = oId;
        formatted.propertyId = propertyId;
        formatted.ownershipNameAr = ownership.ownershipNameAr || ownership.OwnershipNameAr || '';
        formatted.ownershipNameEng = ownership.ownershipNameEng || ownership.OwnershipNameEng || '';
        if (ownership.dob || ownership.Dob) formatted.dob = ownership.dob || ownership.Dob;
        if (parseId(ownership.nationalityId || ownership.NationalityId) !== null) formatted.nationalityId = parseId(ownership.nationalityId || ownership.NationalityId);
        if (parseId(ownership.identityTypeId || ownership.IdentityTypeId) !== null) formatted.identityTypeId = parseId(ownership.identityTypeId || ownership.IdentityTypeId);
        if (ownership.identityNumber || ownership.IdentityNumber) formatted.identityNumber = ownership.identityNumber || ownership.IdentityNumber;
        if (ownership.mobileNumberCountryCode || ownership.MobileNumberCountryCode) formatted.mobileNumberCountryCode = ownership.mobileNumberCountryCode || ownership.MobileNumberCountryCode;
        if (ownership.mobileNumber || ownership.MobileNumber) formatted.mobileNumber = ownership.mobileNumber || ownership.MobileNumber;
        if (ownership.emailId || ownership.EmailId) formatted.emailId = ownership.emailId || ownership.EmailId;
        if (parseId(ownership.bankId || ownership.BankId) !== null) formatted.bankId = parseId(ownership.bankId || ownership.BankId);
        if (ownership.bankAccountNumber || ownership.BankAccountNumber) formatted.bankAccountNumber = ownership.bankAccountNumber || ownership.BankAccountNumber;
        if (parseId(ownership.areaId || ownership.AreaId) !== null) formatted.areaId = parseId(ownership.areaId || ownership.AreaId);
        if (parseId(ownership.cityId || ownership.CityId) !== null) formatted.cityId = parseId(ownership.cityId || ownership.CityId);
        if (parseId(ownership.districtId || ownership.DistrictId) !== null) formatted.districtId = parseId(ownership.districtId || ownership.DistrictId);
        if (ownership.streetName || ownership.StreetName) formatted.streetName = ownership.streetName || ownership.StreetName;
        if (ownership.buildingNumber || ownership.BuildingNumber) formatted.buildingNumber = ownership.buildingNumber || ownership.BuildingNumber;
        if (ownership.additionalNumber || ownership.AdditionalNumber) formatted.additionalNumber = ownership.additionalNumber || ownership.AdditionalNumber;
        if (ownership.postalCode || ownership.PostalCode) formatted.postalCode = ownership.postalCode || ownership.PostalCode;
        formatted.isAgent = (ownership.isAgent || ownership.IsAgent) === true;
        if (ownership.agentNumber || ownership.AgentNumber) formatted.agentNumber = ownership.agentNumber || ownership.AgentNumber;
        if (ownerIdValue !== null) formatted.ownerId = ownerIdValue;
        
        return formatted;
      });
      
      await propertyAPI.saveTab2(propertyId, { 
        ownerships: formattedOwnerships,
        deletedOwnershipIds: deletedIdsArray
      });
      if (deletedIds === null) {
        setDeletedOwnershipIds([]);
      }
      // Reload ownerships to get updated data
      await loadPropertyOwnerships(propertyId);
    } catch (err) {
      console.error('Failed to save property ownerships:', err);
      throw err;
    }
  };

  const loadPropertyOwnerships = async (propId) => {
    try {
      const response = await contractAPI.getPropertyOwnerships(propId);
      setPropertyOwnerships(response.data || []);
    } catch (err) {
      console.error('Failed to load property ownerships:', err);
    }
  };

  // Generate payment dates based on frequency
  const generatePaymentDates = (frequency, startDate, totalRent) => {
    if (!startDate) return;
    
    const dates = [];
    const start = new Date(startDate);
    let currentDate = new Date(start);
    let numberOfInstallments = 0;
    let periodMonths = 0;

    switch (frequency) {
      case 'Monthly':
        numberOfInstallments = 12;
        periodMonths = 1;
        break;
      case 'Quarterly':
        numberOfInstallments = 4;
        periodMonths = 3;
        break;
      case 'Semi-Annual':
        numberOfInstallments = 2;
        periodMonths = 6;
        break;
      case 'Annual':
        numberOfInstallments = 1;
        periodMonths = 12;
        break;
      case 'Custom':
        // For custom, start with 12 monthly installments
        numberOfInstallments = 12;
        periodMonths = 1;
        break;
      default:
        return;
    }

    // Calculate amount per installment
    const amountPerInstallment = totalRent && totalRent > 0 ? (totalRent / numberOfInstallments) : null;

    for (let i = 1; i <= numberOfInstallments; i++) {
      dates.push({
        id: i,
        installmentNumber: i,
        dueDate: currentDate.toISOString().split('T')[0],
        amount: amountPerInstallment
      });
      
      // Move to next period
      currentDate = new Date(currentDate);
      currentDate.setMonth(currentDate.getMonth() + periodMonths);
    }

    setPaymentDates(dates);
  };

  // Load selected property details
  const loadSelectedProperty = async (propId) => {
    try {
      setLoading(true);
      const response = await contractAPI.getPropertiesWithDetails({ propertyId: parseInt(propId) });
      const data = response.data || response || {};
      
      // Handle both array and object responses
      let properties = [];
      if (Array.isArray(data)) {
        properties = data;
      } else if (data.properties) {
        properties = data.properties;
      } else if (data.items) {
        properties = data.items;
      }
      
      if (properties.length > 0) {
        const property = properties[0];
        setSelectedProperty(property);
        setPropertyId(parseInt(propId));
        // Load units and ownerships for this property
        await loadPropertyUnits(propId);
        await loadPropertyOwnerships(propId);
      } else {
        // Try loading directly from property API as fallback
        try {
          const propResponse = await propertyAPI.getById(propId);
          if (propResponse.data) {
            setSelectedProperty(propResponse.data);
            setPropertyId(parseInt(propId));
            await loadPropertyUnits(propId);
            await loadPropertyOwnerships(propId);
          }
        } catch (fallbackErr) {
          console.error('Fallback property load failed:', fallbackErr);
          setErrorMessage(t('contract.create.messages.failedToLoadPropertyDetails'));
          setShowErrorModal(true);
        }
      }
    } catch (err) {
      console.error('Failed to load selected property:', err);
      // Try fallback approach
      try {
        const propResponse = await propertyAPI.getById(propId);
        if (propResponse.data) {
          setSelectedProperty(propResponse.data);
          setPropertyId(parseInt(propId));
          await loadPropertyUnits(propId);
          await loadPropertyOwnerships(propId);
        }
      } catch (fallbackErr) {
        console.error('Fallback property load failed:', fallbackErr);
        setErrorMessage('Failed to load property details.');
        setShowErrorModal(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Tab 1: Save Contract (Select Property)
  const handleSaveTab1 = async (skipSuccessModal = false) => {
    if (!dateFrom || !dateTo) {
      setErrorMessage(t('contract.create.messages.dateFromToRequired'));
      setShowErrorModal(true);
      return false;
    }
    
    if (!propertyId) {
      setErrorMessage(t('contract.create.messages.selectPropertyRequired'));
      setShowErrorModal(true);
      return false;
    }
    
    if (new Date(dateTo) < new Date(dateFrom)) {
      setErrorMessage(t('contract.create.messages.dateToAfterDateFrom'));
      setShowErrorModal(true);
      return false;
    }

    try {
      setLoading(true);
      const data = {
        id: contractId || 0,
        dateFrom,
        dateTo,
        propertyId,
        contractType: contractType
      };
      
      const response = await contractAPI.saveContract(data);
      
      if (response.data.success) {
        setContractId(response.data.contractId);
        setTab1Saved(true);
        
        // Load property units and ownerships
        await loadPropertyUnits(propertyId);
        await loadPropertyOwnerships(propertyId);
        
        if (!skipSuccessModal) {
          setSuccessMessage(t('contract.create.messages.saveSuccess'));
          setShowSuccessModal(true);
        }
        return true;
      } else {
        setErrorMessage(response.data.message || t('contract.create.messages.saveError'));
        setShowErrorModal(true);
        return false;
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('contract.create.messages.saveError'));
      setShowErrorModal(true);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Tab 2: Save Contract Units
  const handleSaveTab2 = async (skipSuccessModal = false) => {
    if (!contractId) {
      setErrorMessage(t('contract.create.messages.saveTab1First'));
      setShowErrorModal(true);
      return false;
    }

    if (selectedUnits.length === 0) {
      setErrorMessage(t('contract.create.messages.selectAtLeastOneUnit'));
      setShowErrorModal(true);
      return false;
    }

    try {
      setLoading(true);
      const data = {
        contractId: parseInt(contractId),
        contractUnits: selectedUnits.map(unit => ({
          propertyUnitId: parseInt(unit.propertyUnitId),
          agreementStatus: unit.agreementStatus === true || unit.agreementStatus === 'true',
          facilities: (unit.facilities || []).map(f => ({
            FacilityId: parseInt(f.FacilityId || f.facilityId),
            Quantity: parseInt(f.Quantity || f.quantity || 1)
          }))
        }))
      };
      
      const response = await contractAPI.saveContractUnits(data);
      
      if (response.data.success) {
        setTab2Saved(true);
        if (!skipSuccessModal) {
        setSuccessMessage(t('contract.create.messages.unitsSaveSuccess'));
        setShowSuccessModal(true);
        }
        return true;
      } else {
        setErrorMessage(response.data.message || t('contract.create.messages.unitsSaveError'));
        setShowErrorModal(true);
        return false;
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('contract.create.messages.unitsSaveError'));
      setShowErrorModal(true);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Tab 3: Save Contract Parties
  const handleSaveTab3 = async (skipSuccessModal = false) => {
    if (!contractId) {
      setErrorMessage('Please save the contract first (Tab 1).');
      setShowErrorModal(true);
      return false;
    }

    if (!lessor) {
      setErrorMessage('Please select a lessor.');
      setShowErrorModal(true);
      return false;
    }

    if (tenants.length === 0) {
      setErrorMessage('Please add at least one tenant.');
      setShowErrorModal(true);
      return false;
    }

    try {
      setLoading(true);
      const data = {
        contractId: parseInt(contractId),
        contractLessor: lessor ? { propertyOwnershipId: parseInt(lessor.propertyOwnershipId) } : null,
        contractTenants: tenants.map(t => ({
          nameAr: t.nameAr || '',
          nameEng: t.nameEng || '',
          dob: t.dob || null,
          nationalityId: t.nationalityId ? parseInt(t.nationalityId) : null,
          identityTypeId: t.identityTypeId ? parseInt(t.identityTypeId) : null,
          identityNumber: t.identityNumber || '',
          mobileNumberCountryCode: t.mobileCountryCode || t.mobileNumberCountryCode || '',
          mobileNumber: t.mobileNumber || '',
          emailId: t.emailId || '',
          areaId: t.areaId ? parseInt(t.areaId) : null,
          cityId: t.cityId ? parseInt(t.cityId) : null,
          districtId: t.districtId ? parseInt(t.districtId) : null,
          streetName: t.streetName || '',
          buildingNumber: t.buildingNumber || '',
          additionalNumber: t.additionalNumber || '',
          postalCode: t.postalCode || '',
          // Commercial fields
          tradeNameAr: t.tradeNameAr || '',
          tradeNameEng: t.tradeNameEng || '',
          crNumber: t.crNumber || '',
          vatNumber: t.vatNumber || '',
          telephoneNumber: t.telephoneNumber || '',
          faxNumber: t.faxNumber || '',
          website: t.website || '',
          unifiedNumber: t.unifiedNumber || '',
          ownerIdType: t.ownerIdType || '',
          facilityType: t.facilityType || '',
          releasedBy: t.releasedBy || '',
          commerceRegistryConfirmationDate: t.commerceRegistryConfirmationDate || null,
          crDate: t.crDate || null,
          commerceActivities: t.commerceActivities || ''
        }))
      };
      
      const response = await contractAPI.saveContractParties(data);
      
      if (response.data.success) {
        setTab3Saved(true);
        if (!skipSuccessModal) {
        setSuccessMessage(t('contract.create.messages.partiesSaveSuccess'));
        setShowSuccessModal(true);
        }
        return true;
      } else {
        setErrorMessage(response.data.message || t('contract.create.messages.partiesSaveError'));
        setShowErrorModal(true);
        return false;
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('contract.create.messages.partiesSaveError'));
      setShowErrorModal(true);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Tab 4: Save Financial Terms
  const handleSaveTab4 = async () => {
    if (!contractId) {
      setErrorMessage(t('contract.create.messages.saveTab1First'));
      setShowErrorModal(true);
      return;
    }

    try {
      setLoading(true);
      // Format PaymentChannels - convert array to JSON string if needed
      let paymentChannelsValue = financialTerms.paymentChannels;
      if (Array.isArray(paymentChannelsValue)) {
        paymentChannelsValue = JSON.stringify(paymentChannelsValue);
      }
      
      const data = {
        contractId,
        ...financialTerms,
        paymentChannels: paymentChannelsValue,
        paymentDates: paymentDates.map(p => ({
          installmentNumber: p.installmentNumber,
          dueDate: p.dueDate,
          amount: p.amount
        }))
      };
      
      const response = await contractAPI.saveFinancialTerms(data);
      
      if (response.data.success) {
        setTab4Saved(true);
        setSuccessMessage(t('contract.create.messages.financialSaveSuccess'));
        setShowSuccessModal(true);
        // Redirect to contract list after successful save (only for create, not edit)
        if (!isEdit && !isView) {
          setTimeout(() => {
            navigate('/contract');
          }, 1500);
        }
      } else {
        setErrorMessage(response.data.message || t('contract.create.messages.financialSaveError'));
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || t('contract.create.messages.financialSaveError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Next Tab
  const handleNextTab = async () => {
    if (activeTab === 1) {
      if (!tab1Saved) {
        const success = await handleSaveTab1(true); // Skip success modal
        if (success) {
          setActiveTab(2);
      }
      } else {
        setActiveTab(2);
      }
    } else if (activeTab === 2) {
      if (!tab2Saved) {
        const success = await handleSaveTab2(true); // Skip success modal
        if (success) {
          setActiveTab(3);
      }
      } else {
        setActiveTab(3);
      }
    } else if (activeTab === 3) {
      if (!tab3Saved) {
        const success = await handleSaveTab3(true); // Skip success modal
        if (success) {
          setActiveTab(4);
      }
      } else {
        setActiveTab(4);
      }
    }
  };

  // Handle Previous Tab
  const handlePreviousTab = () => {
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
    }
  };

  // Handle Property Selection
  const handleSelectProperty = () => {
    // Navigate to property selection page, preserving contract type in returnUrl
    const returnUrl = contractType === 1 
      ? '/contract/create?type=commercial' 
      : '/contract/create?type=residential';
    navigate('/property/select', { state: { returnUrl, contractId, contractType } });
  };

  // Fill Sample Data
  // Check if tab can be accessed
  const canAccessTab = (tabNumber) => {
    if (tabNumber === 1) return true;
    if (tabNumber === 2) return tab1Saved;
    if (tabNumber === 3) return tab2Saved;
    if (tabNumber === 4) return tab3Saved;
    return false;
  };

  // Handle tab click
  const handleTabClick = (tabNumber) => {
    if (!canAccessTab(tabNumber)) {
      return;
    }
    setActiveTab(tabNumber);
  };

  return (
    <div className="page-container">
      {isView && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
          <button className="btn btn-primary" onClick={() => navigate(`/contract/edit/${id}`)}>
            <i className="fas fa-edit"></i> {t('contract.view.edit')}
          </button>
        </div>
      )}

      <div className="property-form-container">
        {/* Tabs Navigation */}
        <div className="property-tabs">
          <div 
            className={`tab-header ${activeTab === 1 ? 'active' : ''} ${!canAccessTab(1) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(1)}
              >
            <i className="fas fa-building"></i>
            <span>{t('contract.create.tabs.tab1')}</span>
                {tab1Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 2 ? 'active' : ''} ${!canAccessTab(2) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(2)}
              >
            <i className="fas fa-door-open"></i>
            <span>{t('contract.create.tabs.tab2')}</span>
                {tab2Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 3 ? 'active' : ''} ${!canAccessTab(3) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(3)}
              >
            <i className="fas fa-users"></i>
            <span>{t('contract.create.tabs.tab3')}</span>
                {tab3Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 4 ? 'active' : ''} ${!canAccessTab(4) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(4)}
              >
            <i className="fas fa-money-bill-wave"></i>
            <span>{t('contract.create.tabs.tab4')}</span>
                {tab4Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
        </div>

        {/* Tab Content */}
        <div className="property-tab-content">
          {/* Tab 1: Select Property */}
          {activeTab === 1 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('contract.create.tabs.tab1')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('contract.create.fields.dateFrom')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      value={dateFrom}
                      readOnly={isView}
                      disabled={isView}
                      onChange={(e) => setDateFrom(e.target.value)}
                      required={!isView}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('contract.create.fields.dateTo')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      readOnly={isView}
                      disabled={isView}
                      required={!isView}
                    />
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header">
                  <h5><i className="fas fa-building text-primary"></i> <span className="text-primary">{t('contract.create.buttons.selectProperty')}</span></h5>
                </div>
                <div className="card-body">
                  {selectedProperty ? (
                    <div className="property-display-card">
                      <div className="row align-items-center">
                        <div className="col-md-10">
                          {/* Row 1: Name (English and Arabic) */}
                          <div className="row mb-2">
                            <div className="col-12">
                              <h5 className="mb-1" style={{ display: 'inline-block', marginRight: '0.5rem' }}>
                                {selectedProperty.nameEng || selectedProperty.NameEng}
                              </h5>
                              {(selectedProperty.nameAr || selectedProperty.NameAr) && (
                                <span className="text-muted small" style={{ fontSize: '0.9rem' }}>
                                  - {selectedProperty.nameAr || selectedProperty.NameAr}
                                </span>
                              )}
                        </div>
                          </div>
                          {/* Row 2: Code, Type, City */}
                          <div className="row mb-2">
                            <div className="col-12 d-flex flex-wrap gap-3 align-items-center">
                              {(selectedProperty.code || selectedProperty.Code) && (
                                <span className="small">
                                  <strong>{t('contract.create.fields.code')}:</strong> {selectedProperty.code || selectedProperty.Code}
                                </span>
                              )}
                              {selectedProperty.realEstateType && (
                                <span className="small">
                                  <strong>{t('contract.create.fields.type')}:</strong> {selectedProperty.realEstateType.nameEng || selectedProperty.realEstateType.NameEng}
                                </span>
                              )}
                              {selectedProperty.city && (
                                <span className="small">
                                  <strong>{t('contract.create.fields.city')}:</strong> {selectedProperty.city.cityNameEng || selectedProperty.city.CityNameEng}
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedProperty.facilities && Array.isArray(selectedProperty.facilities) && selectedProperty.facilities.length > 0 && (
                            <div className="row mt-3">
                              <div className="col-12">
                                <label className="form-label facilities-section-label" style={{ marginBottom: '1rem' }}>
                                  <i className="fas fa-star"></i> {t('contract.create.fields.facilitiesAndAmenities')}
                                </label>
                                <div className="facilities-section-container">
                                  <div className="facilities-grid">
                                    {selectedProperty.facilities.map((facility, idx) => {
                                      const quantity = facility.quantity || facility.Quantity || 1;
                                      return (
                                        <div key={idx} className="facility-card active" style={{ cursor: 'default' }}>
                                          <div className="facility-card-content">
                                            <div className="facility-icon">
                                              <i className="fas fa-check-circle"></i>
                                            </div>
                                            <div className="facility-info">
                                              <div className="facility-name">{facility.nameEng || facility.NameEng || facility}</div>
                                              {facility.nameAr || facility.NameAr ? (
                                                <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                                              ) : null}
                                            </div>
                                            {quantity > 1 && (
                                              <div className="facility-quantity-select">
                                                <div className="facility-quantity-badge">{quantity}</div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="col-md-2 text-center text-md-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleSelectProperty}
                            title={t('contract.create.fields.changeProperty')}
                        >
                            <i className="fas fa-edit"></i>
                        </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    !isView ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSelectProperty}
                      >
                        <i className="fas fa-plus"></i> {t('contract.create.fields.addProperty')}
                      </button>
                    ) : null
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Select Units */}
          {activeTab === 2 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('contract.create.fields.selectUnits')}</h3>
              
              {!propertyId ? (
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle"></i> {t('contract.create.messages.pleaseSelectPropertyFirst')}
                </div>
              ) : units.length === 0 ? (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle"></i> {t('contract.create.messages.noUnitsAvailable')}
                </div>
              ) : (
                <div className="units-grid-modern">
                  {units.map((unit) => {
                    const unitId = unit.id || unit.Id;
                    const isSelected = selectedUnits.some(su => su.propertyUnitId === unitId);
                    const selectedUnit = selectedUnits.find(su => su.propertyUnitId === unitId);
                    return (
                      <div
                        key={unitId}
                        className={`unit-card-modern ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (!isSelected) {
                            setSelectedUnits([...selectedUnits, {
                              propertyUnitId: unitId,
                              agreementStatus: true,
                              facilities: []
                            }]);
                          } else {
                            setSelectedUnits(selectedUnits.filter(su => su.propertyUnitId !== unitId));
                          }
                        }}
                      >
                        <div className="unit-card-header">
                          <div className="unit-card-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setSelectedUnits([...selectedUnits, {
                                    propertyUnitId: unitId,
                                    agreementStatus: true,
                                    facilities: []
                                  }]);
                                } else {
                                  setSelectedUnits(selectedUnits.filter(su => su.propertyUnitId !== unitId));
                                }
                              }}
                            />
                          </div>
                          <div className="unit-card-title">
                            <div className="unit-card-name">
                              <span className="unit-number">{t('contract.create.fields.unit')} {unit.unitNumber || unit.UnitNumber || unitId}</span>
                              {(unit.unitType || unit.UnitType) && (
                                <span className="unit-type">{(unit.unitType?.nameEng || unit.UnitType?.NameEng || unit.unitType?.NameEng || unit.UnitType?.nameEng)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="unit-card-body">
                          <div className="unit-card-details-grid">
                            {(unit.unitArea || unit.UnitArea) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-ruler-combined"></i>
                                <span><strong>{t('contract.create.fields.area')}:</strong> {unit.unitArea || unit.UnitArea} m²</span>
                              </div>
                            )}
                            {(unit.floorNumber || unit.FloorNumber) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-layer-group"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.floor')}:</strong> {unit.floorNumber || unit.FloorNumber}</span>
                              </div>
                            )}
                            {(unit.numberOfRooms || unit.NumberOfRooms) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-door-open"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.rooms')}:</strong> {unit.numberOfRooms || unit.NumberOfRooms}</span>
                              </div>
                            )}
                            {(unit.unitUsage || unit.UnitUsage) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-tag"></i>
                                <span><strong>{t('contract.create.fields.usage')}:</strong> {(unit.unitUsage?.nameEng || unit.UnitUsage?.NameEng || unit.unitUsage?.NameEng || unit.UnitUsage?.nameEng)}</span>
                              </div>
                            )}
                            {(unit.establishedDate || unit.EstablishedDate) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-calendar-alt"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.establishedDate')}:</strong> {new Date(unit.establishedDate || unit.EstablishedDate).toLocaleDateString()}</span>
                              </div>
                            )}
                            {(unit.unitDirection || unit.UnitDirection) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-compass"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.direction')}:</strong> {unit.unitDirection || unit.UnitDirection}</span>
                              </div>
                            )}
                            {(unit.unitFinishing || unit.UnitFinishing) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-paint-brush"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.finishing')}:</strong> {unit.unitFinishing || unit.UnitFinishing}</span>
                              </div>
                            )}
                            {(unit.furnished || unit.Furnished) !== undefined && (
                              <div className="unit-detail-item">
                                <i className="fas fa-couch"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.furnished')}:</strong> {(unit.furnished || unit.Furnished) ? t('common.yes') : t('common.no')}</span>
                              </div>
                            )}
                            {(unit.countOfParking || unit.CountOfParking) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-parking"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.parking')}:</strong> {unit.countOfParking || unit.CountOfParking}</span>
                              </div>
                            )}
                            {(unit.unitFrontLength || unit.UnitFrontLength) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-ruler"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.frontLength')}:</strong> {unit.unitFrontLength || unit.UnitFrontLength} m</span>
                              </div>
                            )}
                            {(unit.unitLength || unit.UnitLength) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-ruler-horizontal"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.length')}:</strong> {unit.unitLength || unit.UnitLength} m</span>
                              </div>
                            )}
                            {(unit.unitWidth || unit.UnitWidth) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-ruler-vertical"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.width')}:</strong> {unit.unitWidth || unit.UnitWidth} m</span>
                              </div>
                            )}
                            {(unit.unitHeight || unit.UnitHeight) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-arrows-alt-v"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.height')}:</strong> {unit.unitHeight || unit.UnitHeight} m</span>
                              </div>
                            )}
                            {(unit.electricityMeter || unit.ElectricityMeter) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-bolt"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.electricityMeter')}:</strong> {unit.electricityMeter || unit.ElectricityMeter}</span>
                              </div>
                            )}
                            {(unit.waterMeter || unit.WaterMeter) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-tint"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.waterMeter')}:</strong> {unit.waterMeter || unit.WaterMeter}</span>
                              </div>
                            )}
                            {(unit.gasMeterNo || unit.GasMeterNo) && (
                              <div className="unit-detail-item">
                                <i className="fas fa-fire"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.gasMeter')}:</strong> {unit.gasMeterNo || unit.GasMeterNo}</span>
                              </div>
                            )}
                            {(unit.includeMezzanine || unit.IncludeMezzanine) !== undefined && (
                              <div className="unit-detail-item">
                                <i className="fas fa-layer-group"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.mezzanine')}:</strong> {(unit.includeMezzanine || unit.IncludeMezzanine) ? t('common.yes') : t('common.no')}</span>
                              </div>
                            )}
                            {(unit.kitchenCabinets || unit.KitchenCabinets) !== undefined && (
                              <div className="unit-detail-item">
                                <i className="fas fa-utensils"></i>
                                <span><strong>{t('contract.create.fields.unitDetails.kitchenCabinets')}:</strong> {(unit.kitchenCabinets || unit.KitchenCabinets) ? t('common.yes') : t('common.no')}</span>
                              </div>
                            )}
                          </div>
                          {unit.facilities && Array.isArray(unit.facilities) && unit.facilities.length > 0 && (
                            <div className="unit-existing-facilities">
                              <div className="facilities-badge-container">
                                {unit.facilities.slice(0, 3).map((facility, idx) => (
                                  <span key={idx} className="facility-badge">
                                    <i className="fas fa-check-circle"></i>
                                    {facility.nameEng || facility.NameEng || facility}
                                  </span>
                                ))}
                                {unit.facilities.length > 3 && (
                                  <span className="facility-badge-more">
                                    +{unit.facilities.length - 3} {t('contract.create.fields.more')}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <div className="unit-card-actions">
                            <div className="unit-facilities-selector">
                              <div className="facilities-selector-header">
                                <i className="fas fa-star"></i>
                                <span>{t('contract.create.fields.selectFacilities')}</span>
                              </div>
                              <div className="facilities-selector-grid">
                                <div className="row g-2" style={{ width: '100%', margin: 0, padding: '0.5rem' }}>
                                      {(dropdowns.unitFacilities || []).map(facility => {
                                        const facilityId = facility.id || facility.Id;
                                        const unitFacilities = selectedUnit?.facilities || [];
                                        const existingFacility = unitFacilities.find(f => (f.FacilityId || f.facilityId) === facilityId);
                                        const isSelected = existingFacility !== undefined;
                                        const quantity = existingFacility ? (existingFacility.Quantity || existingFacility.quantity || 1) : 1;
                                        return (
                                          <div key={facilityId} className="col-md-3" style={{ padding: '0.5rem', width: '100%' }}>
                                            <div
                                              className={`facility-card contract-unit-facility-card ${isSelected ? 'active' : ''}`}
                                              onClick={isView ? undefined : (e) => {
                                                e.stopPropagation();
                                                const updatedUnits = [...selectedUnits];
                                                const unitIndex = updatedUnits.findIndex(su => su.propertyUnitId === (unit.id || unit.Id));
                                                if (unitIndex >= 0) {
                                                  const currentFacilities = updatedUnits[unitIndex].facilities || [];
                                                  if (isSelected) {
                                                    // Remove facility
                                                    updatedUnits[unitIndex] = {
                                                      ...updatedUnits[unitIndex],
                                                      facilities: currentFacilities.filter(f => (f.FacilityId || f.facilityId) !== facilityId)
                                                    };
                                                  } else {
                                                    // Add facility
                                                    updatedUnits[unitIndex] = {
                                                      ...updatedUnits[unitIndex],
                                                      facilities: [...currentFacilities, { FacilityId: facilityId, Quantity: 1 }]
                                                    };
                                                  }
                                                  setSelectedUnits(updatedUnits);
                                                }
                                              }}
                                              style={isView ? { cursor: 'default', pointerEvents: 'none' } : {}}
                                            >
                                              <div className="facility-card-content">
                                                <div className="facility-icon">
                                                  {facility.icon || facility.Icon ? (
                                                    <img src={facility.icon || facility.Icon} alt={facility.nameEng || facility.NameEng} />
                                                  ) : (
                                                    <i className="fas fa-check-circle"></i>
                                                  )}
                                                </div>
                                                <div className="facility-info">
                                                  <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                                                  {(facility.nameAr || facility.NameAr) && (
                                                    <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                                                  )}
                                                </div>
                                                {isSelected && (
                                                  <div className="facility-quantity-select">
                                                    <select
                                                      className="form-select-sm facility-quantity-dropdown"
                                                      value={quantity}
                                                      disabled={!isSelected || isView}
                                                      onClick={(e) => e.stopPropagation()}
                                                      onChange={(e) => {
                                                        e.stopPropagation();
                                                        const newQuantity = parseInt(e.target.value);
                                                        const updatedUnits = [...selectedUnits];
                                                        const unitIndex = updatedUnits.findIndex(su => su.propertyUnitId === (unit.id || unit.Id));
                                                        if (unitIndex >= 0) {
                                                          const currentFacilities = updatedUnits[unitIndex].facilities || [];
                                                          const facilityIndex = currentFacilities.findIndex(f => (f.FacilityId || f.facilityId) === facilityId);
                                                          const newFacilities = [...currentFacilities];
                                                          if (facilityIndex >= 0) {
                                                            newFacilities[facilityIndex] = {
                                                              FacilityId: facilityId,
                                                              Quantity: newQuantity
                                                            };
                                                          } else {
                                                            newFacilities.push({
                                                              FacilityId: facilityId,
                                                              Quantity: newQuantity
                                                            });
                                                          }
                                                          updatedUnits[unitIndex] = {
                                                            ...updatedUnits[unitIndex],
                                                            facilities: newFacilities
                                                          };
                                                          setSelectedUnits(updatedUnits);
                                                        }
                                                      }}
                                                    >
                                                      {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                                        <option key={num} value={num}>{num}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
              )}
            </div>
          )}

          {/* Tab 3: Contract Parties */}
          {activeTab === 3 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('contract.create.fields.contractParties')}</h3>
              
              {/* Lessor Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-end align-items-center mb-3">
                  {propertyId && !isView && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      title={t('contract.create.fields.addLessor')}
                      style={{ padding: '0.5rem 1rem', color: '#bf4f1f', backgroundColor: 'transparent', border: '1px solid #bf4f1f' }}
                      onClick={() => {
                        setLessorFormData({
                          agentNumber: '',
                          nameAr: '',
                          nameEng: '',
                          dob: '',
                          nationalityId: '',
                          identityTypeId: '',
                          identityNumber: '',
                          mobileCountryCode: '',
                          mobileNumber: '',
                          emailId: '',
                          bankId: '',
                          bankAccountNumber: '',
                          areaId: '',
                          cityId: '',
                          districtId: '',
                          streetName: '',
                          buildingNumber: '',
                          additionalNumber: '',
                          postalCode: ''
                        });
                        setEditingOwnershipId(null);
                        setShowAddLessorModal(true);
                      }}
                    >
                      <i className="fas fa-user-plus"></i> {t('contract.create.fields.addLessor')}
                    </button>
                  )}
                </div>
                  {!propertyId ? (
                    <div className="alert alert-warning">
                      <i className="fas fa-exclamation-triangle"></i> {t('contract.create.messages.pleaseSelectPropertyFirst')}
                    </div>
                  ) : propertyOwnerships.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle"></i> {t('contract.create.fields.noPropertyOwnerships')}
                    </div>
                  ) : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>{t('contract.create.tables.nameEnglish')}</th>
                            <th>{t('contract.create.tables.nameArabic')}</th>
                            <th>{t('contract.create.tables.identityNumber')}</th>
                            <th>{t('contract.create.tables.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {propertyOwnerships.map((ownership) => {
                            const ownershipId = ownership.id || ownership.Id;
                            const isSelected = lessor?.propertyOwnershipId === ownershipId;
                            return (
                              <tr key={ownershipId}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      if (isSelected) {
                                        setLessor(null);
                                      } else {
                                        setLessor({ propertyOwnershipId: ownershipId });
                                      }
                                    }}
                                    disabled={isView}
                                  />
                                </td>
                                <td>{ownership.ownershipNameEng || ownership.OwnershipNameEng || t('common.notAvailable')}</td>
                                <td>{ownership.ownershipNameAr || ownership.OwnershipNameAr || '-'}</td>
                                <td>{ownership.identityNumber || ownership.IdentityNumber || '-'}</td>
                                <td>
                                  {!isView && (
                                    <div className="action-buttons-cell">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-edit"
                                        onClick={() => {
                                        setLessorFormData({
                                          agentNumber: ownership.agentNumber || ownership.AgentNumber || '',
                                          nameAr: ownership.ownershipNameAr || ownership.OwnershipNameAr || '',
                                          nameEng: ownership.ownershipNameEng || ownership.OwnershipNameEng || '',
                                          dob: ownership.dob || ownership.Dob || '',
                                          nationalityId: ownership.nationalityId || ownership.NationalityId || '',
                                          identityTypeId: ownership.identityTypeId || ownership.IdentityTypeId || '',
                                          identityNumber: ownership.identityNumber || ownership.IdentityNumber || '',
                                          mobileCountryCode: ownership.mobileNumberCountryCode || ownership.MobileNumberCountryCode || '',
                                          mobileNumber: ownership.mobileNumber || ownership.MobileNumber || '',
                                          emailId: ownership.emailId || ownership.EmailId || '',
                                          bankId: ownership.bankId || ownership.BankId || '',
                                          bankAccountNumber: ownership.bankAccountNumber || ownership.BankAccountNumber || '',
                                          areaId: ownership.areaId || ownership.AreaId || '',
                                          cityId: ownership.cityId || ownership.CityId || '',
                                          districtId: ownership.districtId || ownership.DistrictId || '',
                                          streetName: ownership.streetName || ownership.StreetName || '',
                                          buildingNumber: ownership.buildingNumber || ownership.BuildingNumber || '',
                                          additionalNumber: ownership.additionalNumber || ownership.AdditionalNumber || '',
                                          postalCode: ownership.postalCode || ownership.PostalCode || ''
                                        });
                                        setEditingOwnershipId(ownershipId);
                                        setShowAddLessorModal(true);
                                      }}
                                      title={t('common.edit') + ' ' + t('contract.create.fields.lessor')}
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-delete"
                                      title={t('common.delete')}
                                      onClick={() => {
                                        setConfirmAction(() => async () => {
                                          try {
                                            setLoading(true);
                                            // If this ownership is selected as lessor, clear the selection
                                            if (isSelected) {
                                              setLessor(null);
                                            }
                                            // Track deleted ownership ID if it's an existing ownership (has an ID > 0)
                                            const deletedIds = ownershipId && ownershipId > 0 ? [ownershipId] : [];
                                            // Remove from list
                                            const updatedOwnerships = propertyOwnerships.filter(o => (o.id || o.Id) !== ownershipId);
                                            setPropertyOwnerships(updatedOwnerships);
                                            // Save to backend with the updated array and deleted IDs
                                            await savePropertyOwnerships(updatedOwnerships, deletedIds);
                                            setSuccessMessage(t('contract.create.messages.lessorDeletedSuccessfully'));
                                            setShowSuccessModal(true);
                                            setShowConfirmModal(false);
                                          } catch (err) {
                                            setErrorMessage(err.response?.data?.message || t('contract.create.messages.failedToDeleteLessor'));
                                            setShowErrorModal(true);
                                            setShowConfirmModal(false);
                                          } finally {
                                            setLoading(false);
                                          }
                                        });
                                        setConfirmData({
                                          title: t('contract.create.messages.confirmDeleteLessorTitle') || t('common.confirmDelete'),
                                          message: t('contract.create.messages.confirmDeleteLessor') || t('common.confirmDeleteMessage'),
                                          confirmText: t('common.delete'),
                                          cancelText: t('common.cancel')
                                        });
                                        setShowConfirmModal(true);
                                      }}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>

              {/* Tenants Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-end align-items-center mb-3">
                  {!isView && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ padding: '0.5rem 1rem', color: '#bf4f1f', backgroundColor: 'transparent', border: '1px solid #bf4f1f' }}
                      onClick={() => {
                      setEditingTenantIndex(null);
                      setTenantFormData({
                        nameAr: '',
                        nameEng: '',
                        dob: '',
                        nationalityId: '',
                        identityTypeId: '',
                        identityNumber: '',
                        mobileCountryCode: '',
                        mobileNumber: '',
                        emailId: '',
                        areaId: '',
                        cityId: '',
                        districtId: '',
                        streetName: '',
                        buildingNumber: '',
                        additionalNumber: '',
                        postalCode: '',
                        tradeNameAr: '',
                        tradeNameEng: '',
                        crNumber: '',
                        vatNumber: '',
                        telephoneNumber: '',
                        faxNumber: '',
                        website: '',
                        unifiedNumber: '',
                        ownerIdType: '',
                        facilityType: '',
                        releasedBy: '',
                        commerceRegistryConfirmationDate: '',
                        crDate: '',
                        commerceActivities: ''
                      });
                      setShowTenantModal(true);
                    }}
                    >
                      <i className="fas fa-user-plus"></i> {t('contract.create.fields.addTenant')}
                    </button>
                  )}
                  {!isView && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ marginLeft: '0.75rem' }}
                      onClick={() => {
                        setEditingTenantIndex(null);
                        setTenantFormData({
                          nameAr: '',
                          nameEng: '',
                          dob: '',
                          nationalityId: '',
                          identityTypeId: '',
                          identityNumber: '',
                          mobileCountryCode: '',
                          mobileNumber: '',
                          emailId: '',
                          areaId: '',
                          cityId: '',
                          districtId: '',
                          streetName: '',
                          buildingNumber: '',
                          additionalNumber: '',
                          postalCode: '',
                          tradeNameAr: '',
                          tradeNameEng: '',
                          crNumber: '',
                          vatNumber: '',
                          telephoneNumber: '',
                          faxNumber: '',
                          website: '',
                          unifiedNumber: '',
                          ownerIdType: '',
                          facilityType: '',
                          releasedBy: '',
                          commerceRegistryConfirmationDate: '',
                          crDate: '',
                          commerceActivities: ''
                        });
                        setForceCommercialTenantForm(true);
                        setShowTenantModal(true);
                      }}
                    >
                      <i className="fas fa-building"></i> {t('contract.create.fields.addCommercialTenant')}
                    </button>
                  )}
                </div>
                  {tenants.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle"></i> {t('contract.create.fields.noTenantsAdded')}
                    </div>
                  ) : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th></th>
                            {contractType === 1 ? (
                              <>
                                <th>{t('contract.create.tables.tradeNameEn')}</th>
                                <th>{t('contract.create.tables.tradeNameAr')}</th>
                                <th>{t('contract.create.tables.crNumber')}</th>
                                <th>{t('contract.create.tables.vatNumber')}</th>
                                <th>{t('contract.create.tables.email')}</th>
                                <th>{t('contract.create.tables.actions')}</th>
                              </>
                            ) : (
                              <>
                                <th>{t('contract.create.tables.nameEnglish')}</th>
                                <th>{t('contract.create.tables.nameArabic')}</th>
                                <th>{t('contract.create.tables.identityNumber')}</th>
                                <th>{t('contract.create.tables.mobile')}</th>
                                <th>{t('contract.create.tables.email')}</th>
                                <th>{t('contract.create.tables.actions')}</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {tenants.map((tenant, index) => (
                            <tr key={index}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={true}
                                  readOnly
                                />
                              </td>
                              {contractType === 1 ? (
                                <>
                                  <td>{tenant.tradeNameEng || tenant.nameEng || t('common.notAvailable')}</td>
                                  <td>{tenant.tradeNameAr || tenant.nameAr || t('common.dash')}</td>
                                  <td>{tenant.crNumber || t('common.dash')}</td>
                                  <td>{tenant.vatNumber || t('common.dash')}</td>
                                  <td>{tenant.emailId || t('common.dash')}</td>
                                </>
                              ) : (
                                <>
                                  <td>{tenant.nameEng || t('common.notAvailable')}</td>
                                  <td>{tenant.nameAr || t('common.dash')}</td>
                                  <td>{tenant.identityNumber || t('common.dash')}</td>
                                  <td>
                                    {(tenant.mobileCountryCode || tenant.mobileNumberCountryCode) 
                                      ? `${tenant.mobileCountryCode || tenant.mobileNumberCountryCode} ${tenant.mobileNumber || ''}`.trim() 
                                      : (tenant.mobileNumber || t('common.dash'))}
                                  </td>
                                  <td>{tenant.emailId || t('common.dash')}</td>
                                </>
                              )}
                              <td>
                                {!isView && (
                                  <div className="action-buttons-cell">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-edit"
                                      title={t('common.edit')}
                                      onClick={() => {
                                      setEditingTenantIndex(index);
                                      setTenantFormData({
                                        nameAr: tenant.nameAr || '',
                                        nameEng: tenant.nameEng || '',
                                        dob: tenant.dob || '',
                                        nationalityId: tenant.nationalityId ? String(tenant.nationalityId) : '',
                                        identityTypeId: tenant.identityTypeId ? String(tenant.identityTypeId) : '',
                                        identityNumber: tenant.identityNumber || '',
                                        mobileCountryCode: tenant.mobileCountryCode || tenant.mobileNumberCountryCode || '',
                                        mobileNumber: tenant.mobileNumber || '',
                                        emailId: tenant.emailId || '',
                                        areaId: tenant.areaId ? String(tenant.areaId) : '',
                                        cityId: tenant.cityId ? String(tenant.cityId) : '',
                                        districtId: tenant.districtId ? String(tenant.districtId) : '',
                                        streetName: tenant.streetName || '',
                                        buildingNumber: tenant.buildingNumber || '',
                                        additionalNumber: tenant.additionalNumber || '',
                                        postalCode: tenant.postalCode || '',
                                        tradeNameAr: tenant.tradeNameAr || '',
                                        tradeNameEng: tenant.tradeNameEng || '',
                                        crNumber: tenant.crNumber || '',
                                        vatNumber: tenant.vatNumber || '',
                                        telephoneNumber: tenant.telephoneNumber || '',
                                        faxNumber: tenant.faxNumber || '',
                                        website: tenant.website || '',
                                        unifiedNumber: tenant.unifiedNumber || '',
                                        ownerIdType: tenant.ownerIdType || '',
                                        facilityType: tenant.facilityType || '',
                                        releasedBy: tenant.releasedBy || '',
                                        commerceRegistryConfirmationDate: tenant.commerceRegistryConfirmationDate ? tenant.commerceRegistryConfirmationDate.split('T')[0] : '',
                                        crDate: tenant.crDate ? tenant.crDate.split('T')[0] : '',
                                        commerceActivities: tenant.commerceActivities || ''
                                      });
                                      setShowTenantModal(true);
                                    }}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-delete"
                                    title={t('common.delete')}
                                    onClick={() => {
                                      setConfirmAction(() => () => {
                                        const updated = tenants.filter((_, i) => i !== index);
                                        setTenants(updated);
                                        setSuccessMessage(t('contract.create.messages.tenantDeletedSuccessfully'));
                                        setShowSuccessModal(true);
                                        setShowConfirmModal(false);
                                      });
                                      setConfirmData({
                                        title: t('contract.create.messages.confirmDeleteTenantTitle') || t('common.confirmDelete'),
                                        message: t('contract.create.messages.confirmDeleteTenant') || t('common.confirmDeleteMessage'),
                                        confirmText: t('common.delete'),
                                        cancelText: t('common.cancel')
                                      });
                                      setShowConfirmModal(true);
                                    }}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Tab 4: Financial Terms */}
          {activeTab === 4 && (
            <div className="tab-panel">
              <h3 className="tab-title">{t('contract.create.fields.financialTerms')}</h3>
              
              {/* Financial Terms Section 1: Rental Fees */}
              <div className="mb-4">
                <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-money-bill-wave" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.contractPeriod')}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-money-bill-wave"></i> {t('contract.create.fields.annualRentPerUnit')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-money-bill-wave input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.annualRentPerUnit || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, annualRentPerUnit: parseFloat(e.target.value) || null })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-percent"></i> {t('contract.create.fields.brokerageCommission')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-percent input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.brokerageCommission || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, brokerageCommission: parseFloat(e.target.value) || null })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-dollar-sign"></i> {t('contract.create.fields.totalRent')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-dollar-sign input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.totalRent || ''}
                          onChange={(e) => {
                            const newTotalRent = parseFloat(e.target.value) || null;
                            setFinancialTerms({ ...financialTerms, totalRent: newTotalRent });
                            // Recalculate payment amounts when total rent changes
                            if (financialTerms.paymentFrequency && dateFrom && paymentDates.length > 0) {
                              generatePaymentDates(financialTerms.paymentFrequency, dateFrom, newTotalRent);
                            }
                          }}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-list"></i> {t('contract.create.fields.totalServices')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-list input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.totalServices || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, totalServices: parseFloat(e.target.value) || null })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-calendar-alt"></i> {t('contract.create.fields.paymentFrequency')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-calendar-alt input-icon"></i>
                      <select
                          className="form-select"
                        value={financialTerms.paymentFrequency}
                        onChange={(e) => {
                          const frequency = e.target.value;
                          setFinancialTerms({ ...financialTerms, paymentFrequency: frequency });
                          // Generate payment dates based on frequency
                          if (frequency && dateFrom) {
                            generatePaymentDates(frequency, dateFrom, financialTerms.totalRent);
                          } else {
                            setPaymentDates([]);
                          }
                        }}
                        disabled={isView}
                      >
                          <option value="">{t('contract.create.fields.selectFrequency')}</option>
                        <option value="Monthly">{t('contract.create.fields.monthly')}</option>
                        <option value="Quarterly">{t('contract.create.fields.quarterly')}</option>
                        <option value="Semi-Annual">{t('contract.create.fields.semiAnnual')}</option>
                        <option value="Annual">{t('contract.create.fields.annual')}</option>
                        <option value="Custom">{t('contract.create.fields.custom')}</option>
                      </select>
                    </div>
                    </div>
                    {financialTerms.paymentFrequency && paymentDates.length > 0 && (
                      <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                        <label className="form-label">
                          <i className="fas fa-calendar-check"></i> {t('contract.create.fields.paymentDates')}
                        </label>
                        <div className="table-container" style={{ marginTop: '1rem' }}>
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>{t('contract.create.tables.installmentNumber')}</th>
                                <th>{t('contract.create.tables.dueDate')}</th>
                                <th>{t('contract.create.tables.amount')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentDates.map((payment, index) => (
                                <tr key={payment.id || index}>
                                  <td>{payment.installmentNumber}</td>
                                  <td>
                                    <input
                                      type="date"
                                      className="form-input"
                                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                                      value={payment.dueDate}
                                      onChange={(e) => {
                                        const updated = [...paymentDates];
                                        updated[index].dueDate = e.target.value;
                                        setPaymentDates(updated);
                                      }}
                                      readOnly={isView}
                                      disabled={isView}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-input"
                                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                                      step="0.01"
                                      min="0"
                                      value={payment.amount ? payment.amount.toFixed(2) : ''}
                                      readOnly
                                      placeholder="0.00"
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </div>

              {/* Financial Terms Section 2: Payment Method */}
              <div className="mb-4">
                <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-credit-card" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.rentPaymentMethod')}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-credit-card"></i> {t('contract.create.fields.paymentMethod')}
                      </label>
                      <div className="mb-3">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="paymentMethod"
                            id="paymentMethodOnlineOnly"
                            value="OnlineOnly"
                            checked={financialTerms.paymentMethod === 'OnlineOnly'}
                        onChange={(e) => setFinancialTerms({ ...financialTerms, paymentMethod: e.target.value })}
                          disabled={isView}
                          />
                          <label className="form-check-label" htmlFor="paymentMethodOnlineOnly">
                            {t('contract.create.fields.payOnlineOnly')}
                          </label>
                    </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="paymentMethod"
                            id="paymentMethodOnlineAndBO"
                            value="OnlineAndBO"
                            checked={financialTerms.paymentMethod === 'OnlineAndBO'}
                            onChange={(e) => setFinancialTerms({ ...financialTerms, paymentMethod: e.target.value })}
                            disabled={isView}
                          />
                          <label className="form-check-label" htmlFor="paymentMethodOnlineAndBO">
                            {t('contract.create.fields.payOnlineAndBO')}
                          </label>
                        </div>
                      </div>
                      {financialTerms.paymentMethod === 'OnlineOnly' && (
                        <div className="mt-3">
                          <label className="form-label small">{t('contract.create.fields.paymentChannels')}</label>
                          <div className="d-flex gap-3 flex-wrap">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="paymentChannelMada"
                                checked={Array.isArray(financialTerms.paymentChannels) ? financialTerms.paymentChannels.includes('Mada') : (financialTerms.paymentChannels || '').includes('Mada')}
                                onChange={(e) => {
                                  const channels = Array.isArray(financialTerms.paymentChannels) 
                                    ? [...financialTerms.paymentChannels]
                                    : (financialTerms.paymentChannels || '').split(',').filter(c => c.trim());
                                  if (e.target.checked) {
                                    if (!channels.includes('Mada')) channels.push('Mada');
                                  } else {
                                    const index = channels.indexOf('Mada');
                                    if (index > -1) channels.splice(index, 1);
                                  }
                                  setFinancialTerms({ ...financialTerms, paymentChannels: channels.filter(c => c.trim()) });
                                }}
                                disabled={isView}
                              />
                              <label className="form-check-label" htmlFor="paymentChannelMada">
                                {t('contract.create.fields.mada')}
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="paymentChannelSadad"
                                checked={Array.isArray(financialTerms.paymentChannels) ? financialTerms.paymentChannels.includes('Sadad') : (financialTerms.paymentChannels || '').includes('Sadad')}
                                onChange={(e) => {
                                  const channels = Array.isArray(financialTerms.paymentChannels) 
                                    ? [...financialTerms.paymentChannels]
                                    : (financialTerms.paymentChannels || '').split(',').filter(c => c.trim());
                                  if (e.target.checked) {
                                    if (!channels.includes('Sadad')) channels.push('Sadad');
                                  } else {
                                    const index = channels.indexOf('Sadad');
                                    if (index > -1) channels.splice(index, 1);
                                  }
                                  setFinancialTerms({ ...financialTerms, paymentChannels: channels.filter(c => c.trim()) });
                                }}
                                disabled={isView}
                              />
                              <label className="form-check-label" htmlFor="paymentChannelSadad">
                                {t('contract.create.fields.sadad')}
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="paymentChannelApplePay"
                                checked={Array.isArray(financialTerms.paymentChannels) ? financialTerms.paymentChannels.includes('Apple Pay') : (financialTerms.paymentChannels || '').includes('Apple Pay')}
                                onChange={(e) => {
                                  const channels = Array.isArray(financialTerms.paymentChannels) 
                                    ? [...financialTerms.paymentChannels]
                                    : (financialTerms.paymentChannels || '').split(',').filter(c => c.trim());
                                  if (e.target.checked) {
                                    if (!channels.includes('Apple Pay')) channels.push('Apple Pay');
                                  } else {
                                    const index = channels.indexOf('Apple Pay');
                                    if (index > -1) channels.splice(index, 1);
                                  }
                                  setFinancialTerms({ ...financialTerms, paymentChannels: channels.filter(c => c.trim()) });
                                }}
                                disabled={isView}
                              />
                              <label className="form-check-label" htmlFor="paymentChannelApplePay">
                                {t('contract.create.fields.applePay')}
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-university"></i> {t('contract.create.fields.ibanNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-university input-icon"></i>
                      <input
                        type="text"
                          className="form-input"
                          value={financialTerms.ibanNumber || ''}
                        onChange={(e) => setFinancialTerms({ ...financialTerms, ibanNumber: e.target.value })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder={t('contract.create.placeholders.enterIBANNumber')}
                      />
                    </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-user"></i> {t('contract.create.fields.ibanAccountHolderName')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-user input-icon"></i>
                      <input
                        type="text"
                          className="form-input"
                          value={financialTerms.ibanAccountHolderName || ''}
                        onChange={(e) => setFinancialTerms({ ...financialTerms, ibanAccountHolderName: e.target.value })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder={t('contract.create.placeholders.enterAccountHolderName')}
                      />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>

              {/* Financial Terms Section 3: Additional Fees */}
              <div className="mb-4">
                <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-money-check-alt" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.additionalFees')}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-grid">
                    {/* Security Deposit */}
                    <div className="form-group">
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="securityDepositEnabled"
                          checked={financialTerms.securityDepositEnabled}
                            onChange={(e) => setFinancialTerms({ ...financialTerms, securityDepositEnabled: e.target.checked })}
                            disabled={isView}
                        />
                        <label className="form-check-label" htmlFor="securityDepositEnabled">
                          <strong>{t('contract.create.fields.securityDeposit')}</strong>
                        </label>
                      </div>
                      <p className="text-muted small mb-3">{t('contract.create.fields.securityDepositDescription')}</p>
                      {financialTerms.securityDepositEnabled && (
                        <div className="input-wrapper">
                          <i className="fas fa-shield-alt input-icon"></i>
                          <input
                            type="number"
                            className="form-input"
                            step="0.01"
                            min="0"
                            value={financialTerms.securityDepositAmount || ''}
                            onChange={(e) => setFinancialTerms({ ...financialTerms, securityDepositAmount: parseFloat(e.target.value) || null })}
                            readOnly={isView}
                            disabled={isView}
                            placeholder={t('contract.create.fields.securityDepositAmount')}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Daily Fine */}
                    <div className="form-group">
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="dailyFineEnabled"
                          checked={financialTerms.dailyFineEnabled}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, dailyFineEnabled: e.target.checked })}
                          disabled={isView}
                        />
                        <label className="form-check-label" htmlFor="dailyFineEnabled">
                          <strong>{t('contract.create.fields.dailyFine')}</strong>
                        </label>
                      </div>
                      <p className="text-muted small mb-3">{t('contract.create.fields.dailyFineDescription')}</p>
                      {financialTerms.dailyFineEnabled && (
                        <div className="input-wrapper">
                          <i className="fas fa-exclamation-triangle input-icon"></i>
                          <input
                            type="number"
                            className="form-input"
                            step="0.01"
                            min="0"
                            value={financialTerms.dailyFineAmount || ''}
                            onChange={(e) => setFinancialTerms({ ...financialTerms, dailyFineAmount: parseFloat(e.target.value) || null })}
                            readOnly={isView}
                            disabled={isView}
                            placeholder={t('contract.create.fields.dailyFineAmount')}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Retainer */}
                    <div className="form-group">
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="retainerEnabled"
                          checked={financialTerms.retainerEnabled}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, retainerEnabled: e.target.checked })}
                          disabled={isView}
                        />
                        <label className="form-check-label" htmlFor="retainerEnabled">
                          <strong>{t('contract.create.fields.retainer')}</strong>
                        </label>
                      </div>
                      {financialTerms.retainerEnabled && (
                        <>
                          {financialTerms.retainerAmount > 0 && (
                            <div className="alert alert-info mb-3">
                              <i className="fas fa-info-circle"></i> {t('contract.create.fields.retainerDescription', { amount: financialTerms.retainerAmount || 0 })}
                            </div>
                          )}
                          <div className="input-wrapper mb-2">
                            <i className="fas fa-download input-icon"></i>
                            <input
                              type="number"
                              className="form-input"
                              step="0.01"
                              min="0"
                              value={financialTerms.retainerAmount || ''}
                              onChange={(e) => setFinancialTerms({ ...financialTerms, retainerAmount: parseFloat(e.target.value) || null })}
                              readOnly={isView}
                              disabled={isView}
                              placeholder={t('contract.create.fields.retainerAmount')}
                            />
                          </div>
                          <div className="input-wrapper mb-2">
                            <i className="fas fa-cog input-icon"></i>
                          <select
                              className="form-select"
                              value={financialTerms.retainerDeductionMethod || ''}
                            onChange={(e) => setFinancialTerms({ ...financialTerms, retainerDeductionMethod: e.target.value })}
                            disabled={isView}
                          >
                              <option value="">{t('contract.create.fields.selectRetainerDeductionMethod')}</option>
                              <option value="JointAccount">{t('contract.create.fields.jointAccountFromSEC')}</option>
                            <option value="Other">{t('contract.create.fields.other')}</option>
                          </select>
                          </div>
                          {financialTerms.retainerDeductionMethod === 'JointAccount' && (
                            <div className="input-wrapper">
                              <i className="fas fa-building input-icon"></i>
                            <input
                              type="text"
                                className="form-input"
                              value={financialTerms.jointAccountNumber || ''}
                              onChange={(e) => setFinancialTerms({ ...financialTerms, jointAccountNumber: e.target.value })}
                              readOnly={isView}
                              disabled={isView}
                                placeholder={t('contract.create.placeholders.enterJointAccountNumber')}
                            />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Brokerage Office Fee */}
                    <div className="form-group">
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="brokerageOfficeFeeEnabled"
                          checked={financialTerms.brokerageOfficeFeeEnabled}
                            onChange={(e) => setFinancialTerms({ ...financialTerms, brokerageOfficeFeeEnabled: e.target.checked })}
                            disabled={isView}
                        />
                        <label className="form-check-label" htmlFor="brokerageOfficeFeeEnabled">
                          <strong>{t('contract.create.fields.brokerageOfficeFee')}</strong>
                        </label>
                      </div>
                      <p className="text-muted small mb-3">{t('contract.create.fields.brokerageOfficeFeeDescription')}</p>
                      {financialTerms.brokerageOfficeFeeEnabled && (
                        <>
                          <div className="input-wrapper mb-2">
                            <i className="fas fa-user input-icon"></i>
                          <select
                              className="form-select"
                              value={financialTerms.brokerageOfficeFeePaidBy || ''}
                              onChange={(e) => setFinancialTerms({ ...financialTerms, brokerageOfficeFeePaidBy: e.target.value })}
                              disabled={isView}
                            >
                              <option value="">{t('contract.create.fields.paidBy')}</option>
                            <option value="Lessor">{t('contract.create.fields.lessor')}</option>
                            <option value="Tenant">{t('contract.create.fields.tenant')}</option>
                          </select>
                          </div>
                          <div className="input-wrapper mb-2">
                            <i className="fas fa-building input-icon"></i>
                            <input
                              type="number"
                              className="form-input"
                              step="0.01"
                              min="0"
                              value={financialTerms.brokerageOfficeFeeAmount || ''}
                                onChange={(e) => setFinancialTerms({ ...financialTerms, brokerageOfficeFeeAmount: parseFloat(e.target.value) || null })}
                                readOnly={isView}
                                disabled={isView}
                                placeholder={t('contract.create.fields.brokerageOfficeFeeAmount')}
                              />
                          </div>
                          <div className="input-wrapper">
                            <i className="fas fa-calendar input-icon"></i>
                          <input
                            type="date"
                              className="form-input"
                            value={financialTerms.brokerageOfficeFeeDueDate || ''}
                            onChange={(e) => setFinancialTerms({ ...financialTerms, brokerageOfficeFeeDueDate: e.target.value })}
                            readOnly={isView}
                            disabled={isView}
                          />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </div>

              {/* Financial Terms Section 4: Starting Balance */}
              <div className="mb-4">
                <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-balance-scale" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.contractStartingBalance')}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-wallet"></i> {t('contract.create.fields.totalPaid')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-wallet input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.totalPaidAmount || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, totalPaidAmount: parseFloat(e.target.value) || null })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-list"></i> {t('contract.create.fields.paidServices')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-list input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.paidServicesAmount || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, paidServicesAmount: parseFloat(e.target.value) || null })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-calendar"></i> {t('contract.create.fields.dueRent')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-calendar input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.firstBillDueRent || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, firstBillDueRent: parseFloat(e.target.value) || null })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-list"></i> {t('contract.create.fields.dueServices')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-list input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.firstBillDueServices || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, firstBillDueServices: parseFloat(e.target.value) || null })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-list"></i> {t('contract.create.fields.scheduledAmount')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-list input-icon"></i>
                        <input
                          type="number"
                          className="form-input"
                          step="0.01"
                          min="0"
                          value={financialTerms.scheduledAmount || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, scheduledAmount: parseFloat(e.target.value) || null })}
                          readOnly={isView}
                          disabled={isView}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-calendar"></i> {t('contract.create.fields.startingDate')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-calendar input-icon"></i>
                        <input
                          type="date"
                          className="form-input"
                          value={financialTerms.scheduledStartDate || ''}
                          onChange={(e) => setFinancialTerms({ ...financialTerms, scheduledStartDate: e.target.value })}
                          readOnly={isView}
                          disabled={isView}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation Buttons */}
        {!isView && (
          <div className="tab-navigation">
            <button
              type="button"
              className="btn btn-secondary nav-btn"
              onClick={handlePreviousTab}
              disabled={activeTab === 1}
            >
              {t('contract.create.buttons.previous')}
            </button>
            <div>
              {activeTab < 4 && (
                <button
                  type="button"
                  className="btn btn-primary nav-btn"
                  onClick={handleNextTab}
                  disabled={loading}
                >
                  {t('contract.create.buttons.next')}
                </button>
              )}
              {activeTab === 4 && !isView && (
                <button
                  type="button"
                  className="btn btn-success nav-btn"
                  onClick={handleSaveTab4}
                  disabled={loading}
                >
                  {t('contract.create.buttons.save')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add New Lessor Modal */}
      {showAddLessorModal && (
        <div className="modal-overlay" onClick={() => setShowAddLessorModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-user-plus"></i> {editingOwnershipId ? t('common.edit') + ' ' + t('contract.create.fields.lessor') : t('contract.create.fields.addLessor')}
              </h5>
              <button type="button" className="btn-close" onClick={() => {
                setShowAddLessorModal(false);
                setEditingOwnershipId(null);
                setLessorFormSubmitted(false);
                setLessorFormErrors({});
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid-4">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('contract.create.fields.agentNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.agentNumber ? 'is-invalid' : ''}`}
                      value={lessorFormData.agentNumber}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, agentNumber: e.target.value });
                        if (lessorFormErrors.agentNumber) {
                          setLessorFormErrors({ ...lessorFormErrors, agentNumber: '' });
                        }
                      }}
                      placeholder={t('contract.create.placeholders.enterAgentNumber')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.agentNumber && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.agentNumberRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> {t('contract.create.fields.nameAr')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-language input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.nameAr.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.nameAr}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, nameAr: e.target.value });
                        if (lessorFormErrors.nameAr) {
                          setLessorFormErrors({ ...lessorFormErrors, nameAr: '' });
                        }
                      }}
                      placeholder={t('contract.create.placeholders.enterArabicName')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.nameAr.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nameArRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-font"></i> {t('contract.create.fields.nameEng')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-font input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.nameEng.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.nameEng}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, nameEng: e.target.value });
                        if (lessorFormErrors.nameEng) {
                          setLessorFormErrors({ ...lessorFormErrors, nameEng: '' });
                        }
                      }}
                      placeholder={t('contract.create.placeholders.enterEnglishName')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.nameEng.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nameEngRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> {t('contract.create.fields.dateOfBirth')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.dob ? 'is-invalid' : ''}`}
                      value={lessorFormData.dob}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, dob: e.target.value });
                        if (lessorFormErrors.dob) {
                          setLessorFormErrors({ ...lessorFormErrors, dob: '' });
                        }
                      }}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.dob && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.dobRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-flag"></i> {t('contract.create.fields.nationality')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-flag input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.nationalityId ? 'is-invalid' : ''}`}
                      value={lessorFormData.nationalityId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, nationalityId: e.target.value });
                        if (lessorFormErrors.nationalityId) {
                          setLessorFormErrors({ ...lessorFormErrors, nationalityId: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select')} --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id || nat.Id} value={nat.id || nat.Id}>
                          {nat.nationalityNameEng || nat.NationalityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.nationalityId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nationalityRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card"></i> {t('contract.create.fields.identityType')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-card input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.identityTypeId ? 'is-invalid' : ''}`}
                      value={lessorFormData.identityTypeId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, identityTypeId: e.target.value });
                        if (lessorFormErrors.identityTypeId) {
                          setLessorFormErrors({ ...lessorFormErrors, identityTypeId: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select')} --</option>
                      {(dropdowns.identities || []).map(identity => (
                        <option key={identity.id || identity.Id} value={identity.id || identity.Id}>
                          {identity.identityNameEng || identity.IdentityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.identityTypeId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.identityTypeRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-hashtag"></i> {t('contract.create.fields.identityNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-hashtag input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.identityNumber.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.identityNumber}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, identityNumber: e.target.value });
                        if (lessorFormErrors.identityNumber) {
                          setLessorFormErrors({ ...lessorFormErrors, identityNumber: '' });
                        }
                      }}
                      placeholder={t('contract.create.placeholders.enterIdentityNumber')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.identityNumber.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.identityNumberRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-phone"></i> {t('contract.create.fields.mobileCountryCode')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-phone input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.mobileCountryCode ? 'is-invalid' : ''}`}
                      value={lessorFormData.mobileCountryCode}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, mobileCountryCode: e.target.value });
                        if (lessorFormErrors.mobileCountryCode) {
                          setLessorFormErrors({ ...lessorFormErrors, mobileCountryCode: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select')} --</option>
                      {(dropdowns.countries || []).map(country => (
                        <option key={country.id || country.Id} value={country.phoneCode || country.PhoneCode}>
                          {country.phoneCode || country.PhoneCode} - {country.nameEng || country.NameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.mobileCountryCode && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.mobileCountryCodeRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mobile-alt"></i> {t('contract.create.fields.mobileNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mobile-alt input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.mobileNumber.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.mobileNumber}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, mobileNumber: e.target.value });
                        if (lessorFormErrors.mobileNumber) {
                          setLessorFormErrors({ ...lessorFormErrors, mobileNumber: '' });
                        }
                      }}
                      placeholder={t('contract.create.placeholders.enterMobileNumber')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.mobileNumber.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.mobileNumberRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-envelope"></i> {t('contract.create.fields.emailId')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      className={`form-input ${lessorFormSubmitted && (!lessorFormData.emailId.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lessorFormData.emailId)) ? 'is-invalid' : ''}`}
                      value={lessorFormData.emailId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, emailId: e.target.value });
                        if (lessorFormErrors.emailId) {
                          setLessorFormErrors({ ...lessorFormErrors, emailId: '' });
                        }
                      }}
                      placeholder={t('contract.create.placeholders.enterEmailAddress')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.emailId.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.emailRequired')}
                    </div>
                  )}
                  {lessorFormSubmitted && lessorFormData.emailId.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lessorFormData.emailId) && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.emailInvalid')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-university"></i> {t('contract.create.fields.bank')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-university input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.bankId ? 'is-invalid' : ''}`}
                      value={lessorFormData.bankId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, bankId: e.target.value });
                        if (lessorFormErrors.bankId) {
                          setLessorFormErrors({ ...lessorFormErrors, bankId: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select')} --</option>
                      {(dropdowns.banks || []).map(bank => (
                        <option key={bank.id || bank.Id} value={bank.id || bank.Id}>
                          {bank.nameEng || bank.NameEng || bank.bankNameEng || bank.BankNameEng || ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.bankId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.bankRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-wallet"></i> {t('contract.create.fields.bankAccountNumber')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-wallet input-icon"></i>
                    <input
                      type="text"
                      className={`form-input ${lessorFormSubmitted && !lessorFormData.bankAccountNumber.trim() ? 'is-invalid' : ''}`}
                      value={lessorFormData.bankAccountNumber}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, bankAccountNumber: e.target.value });
                        if (lessorFormErrors.bankAccountNumber) {
                          setLessorFormErrors({ ...lessorFormErrors, bankAccountNumber: '' });
                        }
                      }}
                      placeholder={t('contract.create.placeholders.enterBankAccountNumber')}
                      required
                    />
                  </div>
                  {lessorFormSubmitted && !lessorFormData.bankAccountNumber.trim() && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.bankAccountNumberRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map"></i> {t('contract.create.fields.area')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.areaId ? 'is-invalid' : ''}`}
                      value={lessorFormData.areaId}
                      onChange={(e) => {
                        handleLessorAreaChange(e.target.value);
                        if (lessorFormErrors.areaId) {
                          setLessorFormErrors({ ...lessorFormErrors, areaId: '' });
                        }
                      }}
                      required
                    >
                      <option value="">-- {t('contract.create.fields.select')} --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id || area.Id} value={area.id || area.Id}>
                          {area.areaNameEng || area.AreaNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.areaId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.areaRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> {t('contract.create.fields.city')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.cityId ? 'is-invalid' : ''}`}
                      value={lessorFormData.cityId}
                      onChange={(e) => {
                        handleLessorCityChange(e.target.value);
                        if (lessorFormErrors.cityId) {
                          setLessorFormErrors({ ...lessorFormErrors, cityId: '' });
                        }
                      }}
                      disabled={!lessorFormData.areaId}
                      required
                    >
                      <option value="">-- {lessorFormData.areaId ? t('contract.create.fields.select') : t('contract.create.fields.selectAreaFirst')} --</option>
                      {getFilteredCities(lessorFormData.areaId).map(city => (
                        <option key={city.id || city.Id} value={city.id || city.Id}>
                          {city.cityNameEng || city.CityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.cityId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.cityRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-pin"></i> {t('contract.create.fields.district')} <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-pin input-icon"></i>
                    <select
                      className={`form-select ${lessorFormSubmitted && !lessorFormData.districtId ? 'is-invalid' : ''}`}
                      value={lessorFormData.districtId}
                      onChange={(e) => {
                        setLessorFormData({ ...lessorFormData, districtId: e.target.value });
                        if (lessorFormErrors.districtId) {
                          setLessorFormErrors({ ...lessorFormErrors, districtId: '' });
                        }
                      }}
                      disabled={!lessorFormData.cityId}
                      required
                    >
                      <option value="">-- {lessorFormData.cityId ? t('contract.create.fields.select') : t('contract.create.fields.selectCityFirst')} --</option>
                      {getFilteredDistricts(lessorFormData.cityId).map(district => (
                        <option key={district.id || district.Id} value={district.id || district.Id}>
                          {district.districtNameEng || district.DistrictNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                  {lessorFormSubmitted && !lessorFormData.districtId && (
                    <div className="validation-message">
                      <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.districtRequired')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> {t('contract.create.fields.streetName')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.streetName}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, streetName: e.target.value })}
                      placeholder={t('contract.create.placeholders.enterStreetName')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> {t('contract.create.fields.buildingNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.buildingNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, buildingNumber: e.target.value })}
                      placeholder={t('contract.create.placeholders.enterBuildingNumber')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus"></i> {t('contract.create.fields.additionalNumber')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.additionalNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, additionalNumber: e.target.value })}
                      placeholder={t('contract.create.placeholders.enterAdditionalNumber')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> {t('contract.create.fields.postalCode')}
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.postalCode}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, postalCode: e.target.value })}
                      placeholder={t('contract.create.placeholders.enterPostalCode')}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary modal-btn-equal-width" onClick={() => {
                setShowAddLessorModal(false);
                setLessorFormSubmitted(false);
                setLessorFormErrors({});
              }}>
                {t('contract.create.buttons.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary modal-btn-equal-width"
                onClick={async () => {
                  setLessorFormSubmitted(true);
                  
                  if (!propertyId) {
                    setErrorMessage(t('contract.create.messages.pleaseSelectPropertyFirstLessor'));
                    setShowErrorModal(true);
                    return;
                  }

                  const errors = {};
                  if (!lessorFormData.agentNumber.trim()) {
                    errors.agentNumber = t('contract.create.errors.agentNumberRequired');
                  }
                  if (!lessorFormData.nameAr.trim()) {
                    errors.nameAr = t('contract.create.errors.nameArRequired');
                  }
                  if (!lessorFormData.nameEng.trim()) {
                    errors.nameEng = t('contract.create.errors.nameEngRequired');
                  }
                  if (!lessorFormData.dob) {
                    errors.dob = t('contract.create.errors.dobRequired');
                  }
                  if (!lessorFormData.nationalityId) {
                    errors.nationalityId = t('contract.create.errors.nationalityRequired');
                  }
                  if (!lessorFormData.identityTypeId) {
                    errors.identityTypeId = t('contract.create.errors.identityTypeRequired');
                  }
                  if (!lessorFormData.identityNumber.trim()) {
                    errors.identityNumber = t('contract.create.errors.identityNumberRequired');
                  }
                  if (!lessorFormData.mobileCountryCode) {
                    errors.mobileCountryCode = t('contract.create.errors.mobileCountryCodeRequired');
                  }
                  if (!lessorFormData.mobileNumber.trim()) {
                    errors.mobileNumber = t('contract.create.errors.mobileNumberRequired');
                  }
                  if (!lessorFormData.emailId.trim()) {
                    errors.emailId = t('contract.create.errors.emailRequired');
                  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lessorFormData.emailId)) {
                    errors.emailId = t('contract.create.errors.emailInvalid');
                  }
                  if (!lessorFormData.bankId) {
                    errors.bankId = t('contract.create.errors.bankRequired');
                  }
                  if (!lessorFormData.bankAccountNumber.trim()) {
                    errors.bankAccountNumber = t('contract.create.errors.bankAccountNumberRequired');
                  }
                  if (!lessorFormData.areaId) {
                    errors.areaId = t('contract.create.errors.areaRequired');
                  }
                  if (!lessorFormData.cityId) {
                    errors.cityId = t('contract.create.errors.cityRequired');
                  }
                  if (!lessorFormData.districtId) {
                    errors.districtId = t('contract.create.errors.districtRequired');
                  }

                  if (Object.keys(errors).length > 0) {
                    setLessorFormErrors(errors);
                    return;
                  }

                  setLessorFormErrors({});

                  try {
                    const ownershipData = {
                      propertyId: propertyId,
                      ownershipNameAr: lessorFormData.nameAr,
                      ownershipNameEng: lessorFormData.nameEng,
                      dob: lessorFormData.dob || null,
                      nationalityId: lessorFormData.nationalityId ? parseInt(lessorFormData.nationalityId) : null,
                      identityTypeId: lessorFormData.identityTypeId ? parseInt(lessorFormData.identityTypeId) : null,
                      identityNumber: lessorFormData.identityNumber || null,
                      mobileNumberCountryCode: lessorFormData.mobileCountryCode || null,
                      mobileNumber: lessorFormData.mobileNumber || null,
                      emailId: lessorFormData.emailId || null,
                      bankId: lessorFormData.bankId ? parseInt(lessorFormData.bankId) : null,
                      bankAccountNumber: lessorFormData.bankAccountNumber || null,
                      areaId: lessorFormData.areaId ? parseInt(lessorFormData.areaId) : null,
                      cityId: lessorFormData.cityId ? parseInt(lessorFormData.cityId) : null,
                      districtId: lessorFormData.districtId ? parseInt(lessorFormData.districtId) : null,
                      streetName: lessorFormData.streetName || null,
                      buildingNumber: lessorFormData.buildingNumber || null,
                      additionalNumber: lessorFormData.additionalNumber || null,
                      postalCode: lessorFormData.postalCode || null,
                      isAgent: false,
                      agentNumber: null,
                      ownerId: null
                    };

                    if (editingOwnershipId) {
                      // Update existing ownership
                      ownershipData.id = editingOwnershipId;
                      // Update in local state
                      const updatedOwnerships = propertyOwnerships.map(o => {
                        const oId = o.id || o.Id;
                        if (oId === editingOwnershipId) {
                          return { ...o, ...ownershipData };
                        }
                        return o;
                      });
                      setPropertyOwnerships(updatedOwnerships);
                      // Save to backend
                      await savePropertyOwnerships();
                      setSuccessMessage(t('contract.create.messages.lessorUpdatedSuccessfully'));
                    } else {
                      // Create new ownership
                      const response = await contractAPI.createPropertyOwnership(ownershipData);
                      const createdOwnership = response.data;

                      // Reload ownerships list
                      await loadPropertyOwnerships(propertyId);

                      // Auto-select the newly created ownership
                      setLessor({ propertyOwnershipId: createdOwnership.id || createdOwnership.Id });
                      setSuccessMessage(t('contract.create.messages.lessorCreatedSuccessfully'));
                    }

                    setShowSuccessModal(true);
                    setShowAddLessorModal(false);
                    setEditingOwnershipId(null);
                    setLessorFormSubmitted(false);
                    setLessorFormErrors({});

                    // Reset form
                    setLessorFormData({
                      agentNumber: '',
                      nameAr: '',
                      nameEng: '',
                      dob: '',
                      nationalityId: '',
                      identityTypeId: '',
                      identityNumber: '',
                      mobileCountryCode: '',
                      mobileNumber: '',
                      emailId: '',
                      bankId: '',
                      bankAccountNumber: '',
                      areaId: '',
                      cityId: '',
                      districtId: '',
                      streetName: '',
                      buildingNumber: '',
                      additionalNumber: '',
                      postalCode: ''
                    });
                  } catch (err) {
                    console.error('Failed to save lessor:', err);
                    setErrorMessage(err.response?.data?.message || (editingOwnershipId ? t('contract.create.messages.failedToUpdateLessor') : t('contract.create.messages.failedToCreateLessor')));
                    setShowErrorModal(true);
                  }
                }}
              >
                <i className="fas fa-save"></i> {t('contract.create.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Tenant Modal */}
      {showTenantModal && (
        <div className="modal-overlay" onClick={() => {
          setShowTenantModal(false);
          setForceCommercialTenantForm(false);
        }}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-user-plus"></i> {editingTenantIndex !== null 
                  ? t('common.edit') + ' ' + t('contract.create.fields.tenants')
                  : (forceCommercialTenantForm || contractType === 1)
                    ? t('contract.create.fields.addCommercialTenant')
                    : t('contract.create.fields.addTenant')}
              </h5>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                <button type="button" className="btn-close" onClick={() => {
                  setShowTenantModal(false);
                  setForceCommercialTenantForm(false);
                }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid-4">
                {(() => {
                  const showCommercialForm = forceCommercialTenantForm || contractType === 1;
                  console.log('Tenant Modal - contractType:', contractType, 'forceCommercialTenantForm:', forceCommercialTenantForm, 'showCommercialForm:', showCommercialForm, 'location.search:', location.search);
                  return showCommercialForm;
                })() ? (
                  // Commercial Tenant Form
                  <>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.tradeNameAr')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.tradeNameAr.trim() ? 'is-invalid' : ''}`}
                          value={tenantFormData.tradeNameAr}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, tradeNameAr: e.target.value });
                            if (tenantFormErrors.tradeNameAr) {
                              setTenantFormErrors({ ...tenantFormErrors, tradeNameAr: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterTradeNameArabic')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.tradeNameAr.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nameArRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.tradeNameEng')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.tradeNameEng.trim() ? 'is-invalid' : ''}`}
                          value={tenantFormData.tradeNameEng}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, tradeNameEng: e.target.value });
                            if (tenantFormErrors.tradeNameEng) {
                              setTenantFormErrors({ ...tenantFormErrors, tradeNameEng: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterTradeNameEnglish')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.tradeNameEng.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nameEngRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.commercialRegistrationNumber')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.crNumber.trim() ? 'is-invalid' : ''}`}
                          value={tenantFormData.crNumber}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, crNumber: e.target.value });
                            if (tenantFormErrors.crNumber) {
                              setTenantFormErrors({ ...tenantFormErrors, crNumber: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterCRNumber')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.crNumber.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.crNumberRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.vatNumber')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.vatNumber.trim() ? 'is-invalid' : ''}`}
                          value={tenantFormData.vatNumber}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, vatNumber: e.target.value });
                            if (tenantFormErrors.vatNumber) {
                              setTenantFormErrors({ ...tenantFormErrors, vatNumber: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterVATNumber')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.vatNumber.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.vatNumberRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-hashtag" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.unifiedNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-hashtag input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.unifiedNumber}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, unifiedNumber: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterUnifiedNumber')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-user-tag" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.ownerIdType')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-user-tag input-icon" style={{ color: '#bf4f1f' }}></i>
                        <select
                          className="form-select"
                          value={tenantFormData.ownerIdType}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, ownerIdType: e.target.value })}
                        >
                          <option value="">-- {t('contract.create.fields.select')} --</option>
                          <option value="Single owner">{t('contract.create.fields.singleOwner')}</option>
                          <option value="Multiple owner">{t('contract.create.fields.multipleOwner')}</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.facilityType')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <select
                          className="form-select"
                          value={tenantFormData.facilityType}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, facilityType: e.target.value })}
                        >
                          <option value="">-- {t('contract.create.fields.select')} --</option>
                          <option value="Commercial">{t('contract.create.fields.commercial')}</option>
                          <option value="Industrial">{t('contract.create.fields.industrial')}</option>
                          <option value="Service">{t('contract.create.fields.service')}</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-user-check" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.releasedBy')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-user-check input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.releasedBy}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, releasedBy: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterReleasedBy')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-calendar-check" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.commerceRegistryConfirmationDate')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-calendar-check input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="date"
                          className="form-input"
                          value={tenantFormData.commerceRegistryConfirmationDate}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, commerceRegistryConfirmationDate: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-calendar-alt" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.crDate')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-calendar-alt input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="date"
                          className="form-input"
                          value={tenantFormData.crDate}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, crDate: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">
                        <i className="fas fa-briefcase" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.commerceActivities')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-briefcase input-icon" style={{ color: '#bf4f1f' }}></i>
                        <textarea
                          className="form-input"
                          rows="3"
                          value={tenantFormData.commerceActivities}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, commerceActivities: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterCommerceActivities')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.nationality')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.nationalityId ? 'is-invalid' : ''}`}
                          value={tenantFormData.nationalityId}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, nationalityId: e.target.value });
                            if (tenantFormErrors.nationalityId) {
                              setTenantFormErrors({ ...tenantFormErrors, nationalityId: '' });
                            }
                          }}
                          required
                        >
                          <option value="">-- {t('contract.create.fields.select')} --</option>
                          {(dropdowns.nationalities || []).map(nat => (
                            <option key={nat.id || nat.Id} value={nat.id || nat.Id}>
                              {nat.nationalityNameEng || nat.NationalityNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.nationalityId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nationalityRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.email')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="email"
                          className={`form-input ${tenantFormSubmitted && (!tenantFormData.emailId.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tenantFormData.emailId)) ? 'is-invalid' : ''}`}
                          value={tenantFormData.emailId}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, emailId: e.target.value });
                            if (tenantFormErrors.emailId) {
                              setTenantFormErrors({ ...tenantFormErrors, emailId: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterEmailAddress')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.emailId.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.emailRequired')}
                        </div>
                      )}
                      {tenantFormSubmitted && tenantFormData.emailId.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tenantFormData.emailId) && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.emailInvalid')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-map" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.area')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-map input-icon" style={{ color: '#bf4f1f' }}></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.areaId ? 'is-invalid' : ''}`}
                          value={tenantFormData.areaId}
                          onChange={(e) => {
                            handleTenantAreaChange(e.target.value);
                            if (tenantFormErrors.areaId) {
                              setTenantFormErrors({ ...tenantFormErrors, areaId: '' });
                            }
                          }}
                          required
                        >
                          <option value="">-- {t('contract.create.fields.select')} --</option>
                          {(dropdowns.areas || []).map(area => (
                            <option key={area.id || area.Id} value={area.id || area.Id}>
                              {area.areaNameEng || area.AreaNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.areaId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.areaRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-city" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.city')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-city input-icon" style={{ color: '#bf4f1f' }}></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.cityId ? 'is-invalid' : ''}`}
                          value={tenantFormData.cityId}
                          onChange={(e) => {
                            handleTenantCityChange(e.target.value);
                            if (tenantFormErrors.cityId) {
                              setTenantFormErrors({ ...tenantFormErrors, cityId: '' });
                            }
                          }}
                          disabled={!tenantFormData.areaId}
                          required
                        >
                          <option value="">-- {tenantFormData.areaId ? t('contract.create.fields.select') : t('contract.create.fields.selectAreaFirst')} --</option>
                          {getFilteredCities(tenantFormData.areaId).map(city => (
                            <option key={city.id || city.Id} value={city.id || city.Id}>
                              {city.cityNameEng || city.CityNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.cityId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.cityRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-map-pin" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.district')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-map-pin input-icon" style={{ color: '#bf4f1f' }}></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.districtId ? 'is-invalid' : ''}`}
                          value={tenantFormData.districtId}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, districtId: e.target.value });
                            if (tenantFormErrors.districtId) {
                              setTenantFormErrors({ ...tenantFormErrors, districtId: '' });
                            }
                          }}
                          disabled={!tenantFormData.cityId}
                          required
                        >
                          <option value="">-- {tenantFormData.cityId ? t('contract.create.fields.select') : t('contract.create.fields.selectCityFirst')} --</option>
                          {getFilteredDistricts(tenantFormData.cityId).map(district => (
                            <option key={district.id || district.Id} value={district.id || district.Id}>
                              {district.districtNameEng || district.DistrictNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.districtId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.districtRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.streetName')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.streetName}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, streetName: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterStreetName')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.buildingNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.buildingNumber}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, buildingNumber: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterBuildingNumber')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.additionalNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.additionalNumber}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, additionalNumber: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterAdditionalNumber')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.postalCode')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.postalCode}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, postalCode: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterPostalCode')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.telephoneNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.telephoneNumber}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, telephoneNumber: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterTelephoneNumber')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.mobileNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.mobileNumber}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, mobileNumber: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterMobileNumber')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.faxNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.faxNumber}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, faxNumber: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterFaxNumber')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building" style={{ color: '#bf4f1f' }}></i> {t('contract.create.fields.website')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon" style={{ color: '#bf4f1f' }}></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.website}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, website: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterWebsite')}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  // Residential Tenant Form
                  <>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> {t('contract.create.fields.nameAr')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-language input-icon"></i>
                        <input
                          type="text"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.nameAr.trim() ? 'is-invalid' : ''}`}
                          value={tenantFormData.nameAr}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, nameAr: e.target.value });
                            if (tenantFormErrors.nameAr) {
                              setTenantFormErrors({ ...tenantFormErrors, nameAr: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterArabicName')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.nameAr.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nameArRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-font"></i> {t('contract.create.fields.nameEng')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-font input-icon"></i>
                        <input
                          type="text"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.nameEng.trim() ? 'is-invalid' : ''}`}
                          value={tenantFormData.nameEng}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, nameEng: e.target.value });
                            if (tenantFormErrors.nameEng) {
                              setTenantFormErrors({ ...tenantFormErrors, nameEng: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterEnglishName')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.nameEng.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nameEngRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-calendar"></i> {t('contract.create.fields.dateOfBirth')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-calendar input-icon"></i>
                        <input
                          type="date"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.dob ? 'is-invalid' : ''}`}
                          value={tenantFormData.dob}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, dob: e.target.value });
                            if (tenantFormErrors.dob) {
                              setTenantFormErrors({ ...tenantFormErrors, dob: '' });
                            }
                          }}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.dob && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.dobRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-passport"></i> {t('contract.create.fields.nationality')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-passport input-icon"></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.nationalityId ? 'is-invalid' : ''}`}
                          value={tenantFormData.nationalityId}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, nationalityId: e.target.value });
                            if (tenantFormErrors.nationalityId) {
                              setTenantFormErrors({ ...tenantFormErrors, nationalityId: '' });
                            }
                          }}
                          required
                        >
                          <option value="">-- {t('contract.create.fields.select')} --</option>
                          {(dropdowns.nationalities || []).map(nat => (
                            <option key={nat.id || nat.Id} value={nat.id || nat.Id}>
                              {nat.nationalityNameEng || nat.NationalityNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.nationalityId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.nationalityRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-id-card"></i> {t('contract.create.fields.identityType')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-id-card input-icon"></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.identityTypeId ? 'is-invalid' : ''}`}
                          value={tenantFormData.identityTypeId}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, identityTypeId: e.target.value });
                            if (tenantFormErrors.identityTypeId) {
                              setTenantFormErrors({ ...tenantFormErrors, identityTypeId: '' });
                            }
                          }}
                          required
                        >
                          <option value="">-- {t('contract.create.fields.select')} --</option>
                          {(dropdowns.identities || []).map(identity => (
                            <option key={identity.id || identity.Id} value={identity.id || identity.Id}>
                              {identity.identityNameEng || identity.IdentityNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.identityTypeId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.identityTypeRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-hashtag"></i> {t('contract.create.fields.identityNumber')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-hashtag input-icon"></i>
                        <input
                          type="text"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.identityNumber.trim() ? 'is-invalid' : ''}`}
                          value={tenantFormData.identityNumber}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, identityNumber: e.target.value });
                            if (tenantFormErrors.identityNumber) {
                              setTenantFormErrors({ ...tenantFormErrors, identityNumber: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterIdentityNumber')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.identityNumber.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.identityNumberRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-phone"></i> {t('contract.create.fields.mobileCountryCode')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-phone input-icon"></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.mobileCountryCode ? 'is-invalid' : ''}`}
                          value={tenantFormData.mobileCountryCode}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, mobileCountryCode: e.target.value });
                            if (tenantFormErrors.mobileCountryCode) {
                              setTenantFormErrors({ ...tenantFormErrors, mobileCountryCode: '' });
                            }
                          }}
                          required
                        >
                          <option value="">-- {t('contract.create.fields.select')} --</option>
                          {(dropdowns.countries || []).map(country => (
                            <option key={country.id || country.Id} value={country.phoneCode || country.PhoneCode}>
                              {country.phoneCode || country.PhoneCode} - {country.nameEng || country.NameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.mobileCountryCode && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.mobileCountryCodeRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-mobile-alt"></i> {t('contract.create.fields.mobileNumber')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-mobile-alt input-icon"></i>
                        <input
                          type="text"
                          className={`form-input ${tenantFormSubmitted && !tenantFormData.mobileNumber.trim() ? 'is-invalid' : ''}`}
                          value={tenantFormData.mobileNumber}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, mobileNumber: e.target.value });
                            if (tenantFormErrors.mobileNumber) {
                              setTenantFormErrors({ ...tenantFormErrors, mobileNumber: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterMobileNumber')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.mobileNumber.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.mobileNumberRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-envelope"></i> {t('contract.create.fields.emailId')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-envelope input-icon"></i>
                        <input
                          type="email"
                          className={`form-input ${tenantFormSubmitted && (!tenantFormData.emailId.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tenantFormData.emailId)) ? 'is-invalid' : ''}`}
                          value={tenantFormData.emailId}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, emailId: e.target.value });
                            if (tenantFormErrors.emailId) {
                              setTenantFormErrors({ ...tenantFormErrors, emailId: '' });
                            }
                          }}
                          placeholder={t('contract.create.placeholders.enterEmailAddress')}
                          required
                        />
                      </div>
                      {tenantFormSubmitted && !tenantFormData.emailId.trim() && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.emailRequired')}
                        </div>
                      )}
                      {tenantFormSubmitted && tenantFormData.emailId.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tenantFormData.emailId) && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.emailInvalid')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-map"></i> {t('contract.create.fields.area')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-map input-icon"></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.areaId ? 'is-invalid' : ''}`}
                          value={tenantFormData.areaId}
                          onChange={(e) => {
                            handleTenantAreaChange(e.target.value);
                            if (tenantFormErrors.areaId) {
                              setTenantFormErrors({ ...tenantFormErrors, areaId: '' });
                            }
                          }}
                          required
                        >
                          <option value="">-- {t('contract.create.fields.select')} --</option>
                          {(dropdowns.areas || []).map(area => (
                            <option key={area.id || area.Id} value={area.id || area.Id}>
                              {area.areaNameEng || area.AreaNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.areaId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.areaRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-city"></i> {t('contract.create.fields.city')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-city input-icon"></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.cityId ? 'is-invalid' : ''}`}
                          value={tenantFormData.cityId}
                          onChange={(e) => {
                            handleTenantCityChange(e.target.value);
                            if (tenantFormErrors.cityId) {
                              setTenantFormErrors({ ...tenantFormErrors, cityId: '' });
                            }
                          }}
                          disabled={!tenantFormData.areaId}
                          required
                        >
                          <option value="">-- {tenantFormData.areaId ? t('contract.create.fields.select') : t('contract.create.fields.selectAreaFirst')} --</option>
                          {getFilteredCities(tenantFormData.areaId).map(city => (
                            <option key={city.id || city.Id} value={city.id || city.Id}>
                              {city.cityNameEng || city.CityNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.cityId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.cityRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-map-pin"></i> {t('contract.create.fields.district')} <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-map-pin input-icon"></i>
                        <select
                          className={`form-select ${tenantFormSubmitted && !tenantFormData.districtId ? 'is-invalid' : ''}`}
                          value={tenantFormData.districtId}
                          onChange={(e) => {
                            setTenantFormData({ ...tenantFormData, districtId: e.target.value });
                            if (tenantFormErrors.districtId) {
                              setTenantFormErrors({ ...tenantFormErrors, districtId: '' });
                            }
                          }}
                          disabled={!tenantFormData.cityId}
                          required
                        >
                          <option value="">-- {tenantFormData.cityId ? t('contract.create.fields.select') : t('contract.create.fields.selectCityFirst')} --</option>
                          {getFilteredDistricts(tenantFormData.cityId).map(district => (
                            <option key={district.id || district.Id} value={district.id || district.Id}>
                              {district.districtNameEng || district.DistrictNameEng}
                            </option>
                          ))}
                        </select>
                      </div>
                      {tenantFormSubmitted && !tenantFormData.districtId && (
                        <div className="validation-message">
                          <i className="fas fa-exclamation-circle"></i> {t('contract.create.errors.districtRequired')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-road"></i> {t('contract.create.fields.streetName')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-road input-icon"></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.streetName}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, streetName: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterStreetName')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-building"></i> {t('contract.create.fields.buildingNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-building input-icon"></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.buildingNumber}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, buildingNumber: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterBuildingNumber')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-plus"></i> {t('contract.create.fields.additionalNumber')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-plus input-icon"></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.additionalNumber}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, additionalNumber: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterAdditionalNumber')}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-mail-bulk"></i> {t('contract.create.fields.postalCode')}
                      </label>
                      <div className="input-wrapper">
                        <i className="fas fa-mail-bulk input-icon"></i>
                        <input
                          type="text"
                          className="form-input"
                          value={tenantFormData.postalCode}
                          onChange={(e) => setTenantFormData({ ...tenantFormData, postalCode: e.target.value })}
                          placeholder={t('contract.create.placeholders.enterPostalCode')}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary modal-btn-equal-width" onClick={() => {
                setShowTenantModal(false);
                setForceCommercialTenantForm(false);
                setEditingTenantIndex(null);
                setTenantFormSubmitted(false);
                setTenantFormErrors({});
                setTenantFormData({
                  nameAr: '',
                  nameEng: '',
                  dob: '',
                  nationalityId: '',
                  identityTypeId: '',
                  identityNumber: '',
                  mobileCountryCode: '',
                  mobileNumber: '',
                  emailId: '',
                  areaId: '',
                  cityId: '',
                  districtId: '',
                  streetName: '',
                  buildingNumber: '',
                  additionalNumber: '',
                  postalCode: '',
                  tradeNameAr: '',
                  tradeNameEng: '',
                  crNumber: '',
                  vatNumber: '',
                  telephoneNumber: '',
                  faxNumber: '',
                  website: '',
                  unifiedNumber: '',
                  ownerIdType: '',
                  facilityType: '',
                  releasedBy: '',
                  commerceRegistryConfirmationDate: '',
                  crDate: '',
                  commerceActivities: ''
                });
              }}>
                {t('contract.create.buttons.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary modal-btn-equal-width"
                onClick={() => {
                  setTenantFormSubmitted(true);
                  
                  const errors = {};
                  
                  if (contractType === 1) {
                    // Commercial validation
                    if (!tenantFormData.tradeNameAr.trim()) {
                      errors.tradeNameAr = t('contract.create.errors.nameArRequired');
                    }
                    if (!tenantFormData.tradeNameEng.trim()) {
                      errors.tradeNameEng = t('contract.create.errors.nameEngRequired');
                    }
                    if (!tenantFormData.crNumber.trim()) {
                      errors.crNumber = t('contract.create.errors.crNumberRequired');
                    }
                    if (!tenantFormData.vatNumber.trim()) {
                      errors.vatNumber = t('contract.create.errors.vatNumberRequired');
                    }
                    if (!tenantFormData.nationalityId) {
                      errors.nationalityId = t('contract.create.errors.nationalityRequired');
                    }
                    if (!tenantFormData.emailId.trim()) {
                      errors.emailId = t('contract.create.errors.emailRequired');
                    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tenantFormData.emailId)) {
                      errors.emailId = t('contract.create.errors.emailInvalid');
                    }
                    if (!tenantFormData.areaId) {
                      errors.areaId = t('contract.create.errors.areaRequired');
                    }
                    if (!tenantFormData.cityId) {
                      errors.cityId = t('contract.create.errors.cityRequired');
                    }
                    if (!tenantFormData.districtId) {
                      errors.districtId = t('contract.create.errors.districtRequired');
                    }
                  } else {
                    // Residential validation
                    if (!tenantFormData.nameAr.trim()) {
                      errors.nameAr = t('contract.create.errors.nameArRequired');
                    }
                    if (!tenantFormData.nameEng.trim()) {
                      errors.nameEng = t('contract.create.errors.nameEngRequired');
                    }
                    if (!tenantFormData.dob) {
                      errors.dob = t('contract.create.errors.dobRequired');
                    }
                    if (!tenantFormData.nationalityId) {
                      errors.nationalityId = t('contract.create.errors.nationalityRequired');
                    }
                    if (!tenantFormData.identityTypeId) {
                      errors.identityTypeId = t('contract.create.errors.identityTypeRequired');
                    }
                    if (!tenantFormData.identityNumber.trim()) {
                      errors.identityNumber = t('contract.create.errors.identityNumberRequired');
                    }
                    if (!tenantFormData.mobileCountryCode) {
                      errors.mobileCountryCode = t('contract.create.errors.mobileCountryCodeRequired');
                    }
                    if (!tenantFormData.mobileNumber.trim()) {
                      errors.mobileNumber = t('contract.create.errors.mobileNumberRequired');
                    }
                    if (!tenantFormData.emailId.trim()) {
                      errors.emailId = t('contract.create.errors.emailRequired');
                    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tenantFormData.emailId)) {
                      errors.emailId = t('contract.create.errors.emailInvalid');
                    }
                    if (!tenantFormData.areaId) {
                      errors.areaId = t('contract.create.errors.areaRequired');
                    }
                    if (!tenantFormData.cityId) {
                      errors.cityId = t('contract.create.errors.cityRequired');
                    }
                    if (!tenantFormData.districtId) {
                      errors.districtId = t('contract.create.errors.districtRequired');
                    }
                  }

                  if (Object.keys(errors).length > 0) {
                    setTenantFormErrors(errors);
                    return;
                  }

                  setTenantFormErrors({});
                  
                  if (editingTenantIndex !== null) {
                    const updated = [...tenants];
                    updated[editingTenantIndex] = { ...tenantFormData };
                    setTenants(updated);
                  } else {
                    setTenants([...tenants, { ...tenantFormData }]);
                  }
                  setShowTenantModal(false);
                  setForceCommercialTenantForm(false);
                  setEditingTenantIndex(null);
                  setTenantFormSubmitted(false);
                  setTenantFormData({
                    nameAr: '',
                    nameEng: '',
                    dob: '',
                    nationalityId: '',
                    identityTypeId: '',
                    identityNumber: '',
                    mobileCountryCode: '',
                    mobileNumber: '',
                    emailId: '',
                    areaId: '',
                    cityId: '',
                    districtId: '',
                    streetName: '',
                    buildingNumber: '',
                    additionalNumber: '',
                    postalCode: '',
                    tradeNameAr: '',
                    tradeNameEng: '',
                    crNumber: '',
                    vatNumber: '',
                    telephoneNumber: '',
                    faxNumber: '',
                    website: '',
                    unifiedNumber: '',
                    ownerIdType: '',
                    facilityType: '',
                    releasedBy: '',
                    commerceRegistryConfirmationDate: '',
                    crDate: '',
                    commerceActivities: ''
                  });
                }}
              >
                {t('contract.create.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unit Facilities Modal */}
      {showUnitFacilitiesModal && (
        <div className="modal-overlay" onClick={() => setShowUnitFacilitiesModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-star"></i> {t('contract.create.fields.selectFacilities')}
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowUnitFacilitiesModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="facilities-section-container">
                <div className="row g-3" style={{ width: '100%', margin: 0 }}>
                  {(dropdowns.unitFacilities || []).map(facility => {
                    const facilityId = facility.id || facility.Id;
                    const isSelected = selectedUnitFacilities[facilityId] !== undefined;
                    const quantity = selectedUnitFacilities[facilityId] || 1;
                    return (
                      <div key={facilityId} className="col-md-3" style={{ padding: '0.5rem' }}>
                        <div
                          className={`facility-card contract-unit-facility-card ${isSelected ? 'active' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              const updated = { ...selectedUnitFacilities };
                              delete updated[facilityId];
                              setSelectedUnitFacilities(updated);
                            } else {
                              setSelectedUnitFacilities({ ...selectedUnitFacilities, [facilityId]: 1 });
                            }
                          }}
                        >
                          <div className="facility-card-content">
                            <div className="facility-icon">
                              {facility.icon || facility.Icon ? (
                                <img src={facility.icon || facility.Icon} alt={facility.nameEng || facility.NameEng} />
                              ) : (
                                <i className="fas fa-check-circle"></i>
                              )}
                            </div>
                            <div className="facility-info">
                              <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                              {(facility.nameAr || facility.NameAr) && (
                                <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                              )}
                            </div>
                            <div className="facility-quantity-select">
                              <select
                                className="form-select form-select-sm"
                                value={quantity}
                                disabled={!isSelected}
                                onChange={(e) => {
                                  setSelectedUnitFacilities({
                                    ...selectedUnitFacilities,
                                    [facilityId]: parseInt(e.target.value) || 1
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {[...Array(20)].map((_, i) => (
                                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary-custom" onClick={() => setShowUnitFacilitiesModal(false)}>
                <i className="fas fa-times"></i> {t('contract.create.buttons.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary-custom"
                onClick={() => {
                  if (currentUnitIndex >= 0 && currentUnitIndex < selectedUnits.length) {
                    const updated = [...selectedUnits];
                    updated[currentUnitIndex] = {
                      ...updated[currentUnitIndex],
                      facilities: Object.keys(selectedUnitFacilities).map(facilityId => ({
                        FacilityId: parseInt(facilityId),
                        Quantity: selectedUnitFacilities[facilityId]
                      }))
                    };
                    setSelectedUnits(updated);
                  }
                  setShowUnitFacilitiesModal(false);
                  setSelectedUnitFacilities({});
                  setCurrentUnitIndex(-1);
                }}
              >
                <i className="fas fa-save"></i> {t('contract.create.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
          setConfirmData(null);
        }}
      />
    </div>
  );
};

export default ContractCreate;

